// src/encounter/encounter-shell-view.tsx
// The in-person encounter HOST shell (Encounter Contract Wave 0). 3 content columns:
//   LEFT  — patient-summary rail (≈328px)          [Contract §4: left-rail zone]
//   CENTER— horizontal pill segmented control (DS Segmented) + step content
//   (Vitals: NOT a persistent band. The 2026-07-23 regression — "repeating the vitals template
//   in all the screens" — stays cured: no strip on hpi/risk/ros/docs/ap/wrap-up. NARROWED
//   2026-08-06 by the EncounterScreen design (lines 490-507), which puts a Vitals block at the
//   top of the PHYSICAL EXAM step only, above the PE region groups. PROD-2577 part B: this host
//   no longer mounts that block itself — the Studio-authored `vitals-block` marker resolves
//   through `resolveVitals` (DS `VitalsBlock`, `layout: 'pills'`), same as chief-complaint
//   resolves through `resolveChiefComplaint`. See encounter-shell-view.test.tsx.)
//   RIGHT — the 4-tab Note/Orders/Timeline/Edit drawer (≈416px)  [right-drawer zone]
// The section nav is DATA: it renders the step blocks resolved from the declarative block
// catalog (encounter-blocks.ts), role-filtered — not a hardcoded list. Presentational: no
// data fetch; all state flows in via props.
import { Fragment, cloneElement, isValidElement, useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef, type MutableRefObject, type ReactNode } from 'react';
import {
  EncounterShell,
  type EncounterShellProps,
  PatientSummaryHeader,
  EncounterStepNav,
  QuestionnaireRenderer,
  type QuestionnaireRendererProps,
  VisitInfoStrip,
  type VisitInfoCell,
  type PatientHeaderData,
  type PatientHeaderDemographics,
  type EncounterStepKey,
  type EncounterStepNavStep,
  type Questionnaire,
  type QuestionnaireResponse,
  type QItem,
  EncounterStepPanel,
  EncounterStepSection,
  EncounterStepEmpty,
  railAbbreviation,
  Callout,
  Banner,
  Button,
  StatusChip,
  authorRoleLabel,
  type EncounterShellSaveState,
} from '@scripsteam/scrips-signal-ds';
import { stepLabel, ALLIED_PLAN_STEP, NURSING_PLAN_STEP, type PaStepKey } from './lib/encounter-steps';
import { EncounterStepBar } from './encounter-step-bar';
import { IntelRailSignContext } from './intelligence/intel-rail-close';
import { nextStepOf } from './lib/encounter-step-bar';
import { IntelligenceFab } from './intelligence-rail';
import { IntelligenceCloseContext } from './intelligence/rail-close';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import type { StepTitleOverrides } from './lib/cps-layout';


/**
 * Section titles per the EncounterScreen design, where they differ from the DS defaults.
 * The DS titles a section from its `kind` ("History Of Present Illness", "Review Of Systems");
 * the design names the HPI screen after what it captures — "Chief Complaint" (line 338) — and
 * lower-cases the "of" in "Review of Systems" (line 423). Steps absent here keep the DS default.
 */
const DESIGN_SECTION_TITLE: Partial<Record<EncounterStepKey, string>> = {
  hpi: 'Chief Complaint',
  ros: 'Review of Systems',
};

/** The steps whose header row the DS renderer's `sectionRail` draws (PROD-2577), and the tab
 *  Clear label each one carries — production's `"${S.current.clear} ${shortTitle}"`
 *  (`common_encounter_component_base_tab.dart:611-614`). */
type RailStep = 'ros' | 'pe';
const RAIL_CLEAR_LABEL: Record<RailStep, string> = { ros: 'Clear ROS', pe: 'Clear PE' };
/** The tab Clear confirm body, verbatim from production's `onClearTap` (`:622-630`) — the
 *  source reads "want clear", without "to"; kept as shipped rather than silently corrected. The
 *  title (the Clear label), "Clear" and "Cancel" are the DS defaults and match production. */
const RAIL_CLEAR_CONFIRM_BODY = 'Are you sure you want clear all information?';

import { formatVisitTime } from './lib/visit-time';
import { formatDateTimeD45, parseNoZIsoDatetime } from '@/shared/lib/date-format';
import { NoteStatusLine } from './note-status-line';
import { NoteSignActions } from './note-sign-actions';
import { NoteSignBar } from './note-sign-bar';
import { goToSignBlank, signBlanks } from './structured-note/sign-blanks';
import { scrollJumpTarget } from './structured-note/jump-focus';
import { useVisitNoteV2 } from './structured-note/label-parts-model';
import { footSendOrdersAction } from './lib/foot-send-orders';
import { isSignedRefusalSentence, NOT_SAVED } from './lib/signed-refusal';
import { signActionState } from './lib/sign-action-state';
import { ChartCardHiddenContext } from './lib/patient-identity';
import { LeftPanelToggle, useLeftPanelFold } from './left-panel-fold';
import { chooseContext, choosePanel, isZoningOn, useEncounterZoning } from './encounter-zoning';
import { resolveSteps, toClinicalRole, type ShellRole, type SuppressibleStepKey } from './lib/encounter-blocks';
import { AlliedHealthPlanView, type AlliedHealthPlanViewProps } from './allied-health-plan-view';
import { DocumentsChartTab, type DocumentsChartTabProps } from '@/patients/components/chart/documents-chart-tab';
import { NursingPlanView, type NursingPlanViewProps } from './nursing-plan-view';
import { nursingFindings, type NursingProblem } from './lib/nursing-problems';
import {
  isQuestionnaireStep,
  questionnaireForStep,
  stepHasContent,
  visibleSteps,
  mergeSectionResponse,
  groupHasAnswer,
  findNarrativeItem,
  stripNarrative,
  holdsOnlyNarrative,
  answerString,
  setAnswer,
  withSymptomChipDefaults,
  isFlatGroupShape,
  sectionSymptomChipIds,
  fillSectionNegative,
  fillGroupNegative,
  railGroups,
  findGroupDeep,
} from './lib/questionnaire-groups';
import { EncounterSectionChromeHost } from './encounter-section-chrome';
import { AssessmentPlanView, type AssessmentPlanViewProps } from './assessment-plan-view';
import { WrapUpView, type WrapUpViewProps } from './wrap-up-view';
import { findChiefComplaintHpiMarker, stripHpiSampleCategories } from './lib/chief-complaint-hpi';
import { SoapRibbon } from './soap-ribbon';
import { useEncOverlayScope } from './enc-overlay-scope';
import { isPassableStep, StepPassContext, type StepPass } from './lib/step-pass';
import { DEFAULT_NOTE_MODE, sectionOf, sectionStepsFor, sectionsFor, type ApPart, type NoteMode, type SoapSectionId } from './lib/soap-ribbon';
import { alliedExtraSections, alliedSoapLayout } from './lib/allied-soap';
import { StructuredNoteSection, type StructuredNotePhrases } from './structured-note/structured-note-section';
import { InsertedTemplatesHost, InsertedTemplatesSlot } from './inserted-templates/inserted-templates-host';
import { InsertedTemplatesPanel } from './inserted-templates/inserted-templates-panel';
import type { InsertedTemplatesConfig } from './inserted-templates/use-inserted-templates';
import type { StructuredHistoryNote } from './structured-note/history-notes-line';
import { completedSections } from './structured-note/section-completion';
import { OBJECTIVE_VITALS_MARKER, objectiveHasVitalsMarker, type VitalsReadState } from './structured-note/objective-vitals-model';
import type { RiskManaged } from './risk/lib/risk-qr-bridge';
import type { ChartLaunchContext } from './lib/chart-population';
import { answerMap, vitalsMarkers } from './structured-note/note-adapter';
import { SECTION_ATTR, useNoteScroll } from './use-note-scroll';
import { systemOrderFor, type ChiefComplaintRef } from './complaint-map/system-order-for';
import { confirmNoteViewChange } from './services/unsaved-note';

export interface EncounterShellViewProps {
  patient: PatientHeaderData;
  demographics?: PatientHeaderDemographics;
  /**
   * The coarse role prop. WIDENED by PROD-2040 with `'allied-health'`; `'doctor'` and `'nurse'`
   * are unchanged and resolve to exactly the flows they did before (see `toClinicalRole`).
   */
  role: ShellRole;
  /** The templates a clinician may add from the "/" menu, and where their answer sets live. Present → the view runs the
   *  added templates (their reads, writes and blocks) and the note's "/" lists them; absent → none. */
  insertedTemplates?: InsertedTemplatesConfig;
  /**
   * `PaStepKey` = the DS `EncounterStepKey` union WIDENED by the PA-local step keys
   * (lib/encounter-steps.ts). Every DS step key is still assignable, so an existing caller that
   * hands these an `EncounterStepKey` needs no change.
   */
  currentStep: PaStepKey;
  completedSteps?: PaStepKey[];
  onStepChange: (step: PaStepKey) => void;
  /**
   * Type-A HPI questionnaire (Template Studio). OPTIONAL: when no template is
   * authored for the visit the BFF returns 200 [] and there is no questionnaire.
   * A/P + Wrap-Up do NOT depend on it, so the shell still mounts — only the HPI
   * step degrades to an in-pane "configure in Template Studio" notice instead of
   * blocking the whole encounter (template-gate-relax). When absent the HPI render
   * path is short-circuited (no renderer crash).
   */
  questionnaire?: Questionnaire;
  /**
   * AUTHOR-CONTROLLED step titles (the questionnaire's `cps-layout` extension → `stepTitles`).
   * OPTIONAL and per-key: any of `hpi` / `ros` / `pe` / `risk` may be renamed by the template
   * author; every other step, and every absent/blank key, keeps its built-in STEP_LABELS value.
   * The page parses and sanitizes this (encounter/lib/cps-layout.ts) — the view only renders it,
   * as a text child, so authored markup is escaped rather than executed.
   */
  stepTitles?: StepTitleOverrides;
  /**
   * AUTHOR-REMOVED steps (the questionnaire's `cps-layout` extension → `hiddenSteps`). OPTIONAL
   * and ADDITIVE: only `hpi` / `risk` / `ros` / `pe` may ever appear here — the page's
   * `hiddenStepsOf` (encounter/lib/cps-layout.ts) already strips anything else, notably `ap`,
   * which stays physician-only and order-bearing and can never be removed. Absent/empty leaves the
   * role-resolved nav untouched. Threaded straight into `resolveSteps`'s `EncounterContext`, which
   * intersects it with role/venue visibility — the template only narrows, never widens or reorders.
   */
  hiddenSteps?: readonly SuppressibleStepKey[];
  /**
   * D7 (Samer decision strip 2026-07-22, PROD-1588): the ACTIVE template's real name —
   * rendered as the read-only pill beside each questionnaire section title (the frame's
   * "Default" pill position, E1 613:3013). HONEST: the page supplies it only when the
   * 5-axis template selection served a named template; absent → no pill.
   */
  templateName?: string;
  /**
   * The visit's type from its appointment (`visitTypeFor`, lib/reason-for-visit.ts): a first visit or a follow-up. With
   * `visitNoteV2` the note opens with one read-only line naming the template and this type (Samer's Loom, 3 Nov: "I cannot
   * see what type of visit"). Absent → the line names the template only.
   */
  visitType?: 'first' | 'follow-up';
  response: QuestionnaireResponse;
  onResponseChange: (r: QuestionnaireResponse) => void;
  /**
   * PROD-2577 (closes PROD-2354): the Risk step renders the template's Fall/Pain groups through
   * the SAME renderer as HPI/ROS/PE; the page mirrors their answers to EncounterMetaData. False
   * while the stored answers are still being merged into `response` — the renderer is then NOT
   * mounted on the Risk step, because Base UI fixes a control's controlled mode at mount and a
   * field first drawn empty would ignore the answer that arrives next render. Absent = ready
   * (Storybook / no-data paths).
   */
  riskReady?: boolean;
  /** Which stored Risk assessment could not be read. The step still renders; this names what will
   *  not be saved, since a failed list is never written back (it would overwrite the stored one). */
  riskReadFailed?: { fall: boolean; pain: boolean };
  /**
   * PROD-2577 — the Fall/Pain risk cards' "Done" button (DS `QuestionnaireRenderer` `onRiskDone`,
   * capture mode only — scrips-signal-ds#401, not yet released). Forwarded verbatim to the
   * renderer; `useRiskBridge` owns what it does (marks the head entry completed through the same
   * local-then-batched-save path every Risk edit uses). Passed unconditionally, like
   * `resolveChiefComplaint`/`resolveVitals` above — the DS only calls it on the Fall/Pain cards.
   * Typed by hand, NOT `QuestionnaireRendererProps['onRiskDone']` — the installed DS
   * (`@scripsteam/scrips-signal-ds@2.7.0`) predates #401, so that key does not exist yet.
   */
  onRiskDone?: (kind: 'fall' | 'pain') => void;
  /**
   * Signed/locked encounter (I2 gate): suppresses the section-level All-Neg/All-Normal bulk
   * action — a finalized note must not be bulk-mutated (the page's autosave is equally gated
   * on `!signed`, so an ungated link would edit state that never persists). The page passes
   * its sign-lock state; absent (Storybook/no-data) = not signed.
   */
  signed?: boolean;
  /**
   * D8 AUTOSAVE (Samer decision strip 2026-07-22, PROD-1588): persistence is debounced
   * per-step autosave owned by the page — the dirty-gated Save button is RETIRED. The view
   * renders only the unobtrusive save-state indicator: `saving` ("Saving…", muted) /
   * `saved` (DS HelperText tone="success", "Saved") / `saveError` (red).
   */
  saving?: boolean;
  /** True when the response is clean AND a persisted row exists — renders the quiet "Saved" mark. */
  saved?: boolean;
  saveError?: string;
  /**
   * Neutral transient status (e.g. "Preparing encounter…") — a loading/notice
   * state, NOT an error. Rendered muted, distinct from `saveError` (red), so a
   * transient condition isn't styled as a failure (PR #9 review POLISH, PROD-1160).
   */
  status?: string;
  /**
   * Assessment & Plan props for the 'ap' step. OPTIONAL by design: when supplied, the
   * 'ap' step renders the A/P canvas (AssessmentPlanView → DS AssessmentPlanCanvas);
   * when absent, the step keeps its "ships next" placeholder.
   */
  assessmentPlan?: AssessmentPlanViewProps;
  /**
   * ALLIED-HEALTH PLAN props for the `allied-plan` step (PROD-2040). OPTIONAL, the SAME pattern as
   * `assessmentPlan` above: supplied → the step renders `AlliedHealthPlanView`; absent → the step
   * keeps the shared "ships next" placeholder.
   *
   * A SEPARATE prop from `assessmentPlan`, deliberately. A/P carries orders and this does not, so
   * one prop serving both would put an order-bearing surface one boolean away from a role that
   * cannot prescribe. Two props, two step keys, two roles — and A/P stays hard-coded physician-only
   * per the founder ruling, not made configurable by this.
   */
  alliedPlan?: AlliedHealthPlanViewProps;
  /** [PROD-2949] The nurse's plan, drawn on its own step (`nursing-plan`, design desk D-66). The page
   *  supplies it only for a nurse with `?ef=planSave` on; absent → the step keeps the "ships next"
   *  placeholder (and the page hides the step itself while the switch is off). */
  nursingPlan?: NursingPlanViewProps;
  /**
   * Wrap-Up step composite (PROD-1234 family). OPTIONAL, same pattern as
   * `assessmentPlan`: each section renders only when supplied; when absent the step
   * keeps its "ships next" placeholder.
   */
  wrapUp?: WrapUpViewProps;
  /**
   * PROD-2577 part B — PE-STEP vitals (EncounterScreen design lines 490-507), now a RESOLVER
   * like `resolveChiefComplaint`. NARROWS — does not revert — the 2026-07-23 "no vitals on
   * every step" cure: the Studio-authored `vitals-block` marker sits above the PE region groups
   * ONLY, and the DS renderer calls this for that one group. Resolved props win over any
   * authored `blockProps`; `undefined` degrades to the DS's own honest-empty render (no
   * fabricated values, no all-dash placeholder band) — same degrade `resolveChiefComplaint`
   * follows when unwired.
   */
  resolveVitals?: QuestionnaireRendererProps['resolveVitals'];
  /**
   * PROD-2136 — the chief-complaint HPI BLOCK's data + handlers: the per-complaint attribute
   * groups (`templateItem`) and saved answers (`patientHpi`) from the SAME complaint document the
   * `chiefComplaint` editor writes, plus the chip-toggle handler and the document's query states.
   * Rendered ONLY where the HPI section authors the Studio marker group (`hpi-chief-complaint` /
   * itemControl `chief-complaint-hpi`) — in place of that group. A template without the marker
   * renders exactly as before, whatever is passed here. Absent while the marker IS authored →
   * the block renders its honest empty state (no complaint known to this host).
   */
  resolveChiefComplaint?: QuestionnaireRendererProps['resolveChiefComplaint'];
  /**
   * PROD-2789 — the encounter's chief complaints as {code, display}, in document order (the page
   * reads them off the SAME complaint document `resolveChiefComplaint` draws). They only SUGGEST
   * which ROS / exam systems open first (rule A9): nothing is hidden, nothing is answered, and a
   * change re-orders without clearing. Absent, empty or unmapped → the authored order.
   */
  chiefComplaints?: readonly ChiefComplaintRef[];
  /**
   * In-encounter header visit-info strip (Flutter add_encounter_detail_widget.dart:133-138,
   * `roomInfo` + `visitReasonInfo` to the LEFT of the tab strip): a ROOM/TIME block (room name as
   * the caption, "Today, h:mm" as the value) + a "Reason for Visit" block. OPTIONAL — each field is
   * shown only when present (Flutter falls back to empty strings). `room` requires an appointment
   * detail fetch (list endpoints return only roomId), so it may be absent; time+reason are on the
   * page. When none resolve, no strip renders.
   */
  visitInfo?: { room?: string; startISO?: string; reason?: string };
  /**
   * "Visit details" drill (design line 305-307): the chevron after the visit-info cells.
   * OPTIONAL and honest — the DS `VisitInfoStrip` renders its chevron only when a handler is
   * supplied, so an unwired host still gets no decorative affordance (the anti-fiction rule
   * that kept Flutter's save-toggle chevron out of here).
   */
  onVisitDetails?: () => void;
  /**
   * LEFT column (Contract §4 left-rail zone): the full PatientSummaryRail — patient card +
   * INTERACTIVE IPS + occupation/education — reused from the pre-encounter rail (Flutter
   * renders the SAME patientSummaryWidget in-encounter). When absent, the shell falls back
   * to the bare PatientSummaryHeader banner (Storybook / no-data paths).
   */
  patientSummary?: ReactNode;
  /**
   * RIGHT column (Contract §4 right-drawer zone): the 4-tab Note/Orders/Timeline/Edit drawer
   * (EncounterDrawer). The page builds it with the live generated note + orders summary +
   * encounterId. OPTIONAL so Storybook/no-data paths can omit it; the gate render supplies it.
   */
  drawer?: ReactNode;
  /**
   * CONTROLLED band disclosure (Flutter `isPatientInfoViewExpanded`). The page owns it when it
   * also needs to drive the summary's CONTENT mode — it forwards the same value to
   * `PatientSummaryRail`'s `expanded`, so one chevron resizes the band and switches its cards
   * between summary lines and per-entry rows, as Flutter does. Omit for uncontrolled.
   */
  summaryExpanded?: boolean;
  onSummaryExpandedChange?: (expanded: boolean) => void;
  /**
   * Docs step content (Contract §4 Docs block → DocumentReference). OPTIONAL slot: when supplied,
   * the 'docs' step renders it (the page passes the documents surface — list + add/upload, which
   * fetches, so it stays out of this presentational view); when absent the step keeps its
   * "ships next" placeholder.
   */
  docs?: ReactNode;
  /**
   * D9 NAVFIRST (Samer decision strip 2026-07-22, PROD-1588): the encounter/chart TOP BAR
   * (DS EncounterTopBar — back + Encounter|Chart + search) mounts on ALL encounter surfaces,
   * not just pre-encounter. OPTIONAL slot rendered at the top of the MAIN COLUMN — per the
   * shell frame 1736:153260 the strip spans the main content only, with the right drawer
   * full-height beside it (top-of-screen alignment fix, Samer live-review 2026-07-23). The
   * page builds PreEncounterTabStrip with its router wiring, keeping navigation out of this
   * presentational view; absent → the pre-D9 layout (Storybook/no-data paths).
   */
  topBar?: ReactNode;
  /**
   * Virtual-visit Connect / Leave banner (the PA `VirtualVisitNotice`). OPTIONAL slot rendered
   * directly under the top bar, mirroring Flutter, which draws its `videoStartWidget` on the
   * started encounter as well as the pre-encounter (encounter_screen.dart:973 and :906). The page
   * owns the call state and whether telemedicine is configured at all, so an in-person visit
   * simply passes nothing and this renders no row.
   */
  virtualVisitNotice?: ReactNode;
  /**
   * Pediatric GrowthChart (PROD-1324) — the longitudinal value-for-age percentile graph. OPTIONAL
   * slot rendered directly under the vitals band (top of center) when supplied; the page builds it
   * (DS `GrowthChart` fed by the per-LOINC vitals history) and gates it to a pediatric patient, so
   * the fetch + age gating stay out of this presentational view. The head-circumference *vital*
   * still renders in the VitalsRow (PROD-1274) — this is the chart, not the row.
   */
  growthChart?: ReactNode;
  /**
   * PROD-2608 — the reopened note's template notice (`TemplateDriftNotice`): a banner when the form
   * is not the template the note was started on, plus the read-only list of saved answers the form
   * has no question for. Rendered above the step canvas on every step, so it cannot be missed by
   * staying on one tab. The component renders nothing when there is nothing to say.
   */
  templateNotice?: ReactNode;
  /** `ambientWindow`: the ambient "Start listening" card, its own row above the note, right-aligned (frame v2). */
  ambientCard?: ReactNode;
  /**
   * G11 — the encounter template could not be READ (not "no template matched"). The page keeps the
   * shell up so A/P and Wrap-up stay usable, and says so above the canvas (with Retry); the steps
   * the template draws (HPI · Risk · ROS · PE) then say the template failed to load instead of the
   * "Not documented" stub, which would read as an empty section (R6: a failed read says so).
   */
  templateUnavailable?: boolean;
  /**
   * "Add Order" — opens the shared left drawer. Supplied by the page for EVERY step except
   * Wrap-up; absent leaves the cluster icon disabled, which is Wrap-up's correct state.
   */
  onAddOrder?: () => void;
  /** "Add to favourites" — opens the centre modal. Same availability rule as onAddOrder. */
  onAddToFavourites?: () => void;
  /**
   * Chart tab body (PROD-1986) — rendered IN PLACE of the step chrome + canvas when
   * `activeTab === 'chart'`. The top bar, the patient-summary banner and the right Note/Orders
   * drawer stay mounted, exactly as Flutter's `encounterView` (encounter_screen.dart:1078-1206)
   * swaps only the TabBarView child for `ChartMainView` (:1049-1057). OPTIONAL slot.
   */
  chart?: ReactNode;
  /** The Documents tab body (this visit's documents): shown in the centre when `activeTab === 'documents'`. */
  documents?: ReactNode;
  /** The Timeline tab body (the visit's care-pathway history): shown in the centre when `activeTab === 'timeline'`. */
  timeline?: ReactNode;
  /** The Terrain tab body (`?x=terrain`): shown in the centre when `activeTab === 'terrain'`. Absent → never shown. */
  terrain?: ReactNode;
  /**
   * Which top tab is active (PROD-1986). Defaults to 'encounter'; 'chart' shows the `chart` slot, 'documents' the `documents` slot.
   * The page owns the selection (an encounter-route search param) — this view stays presentational.
   */
  activeTab?: 'encounter' | 'terrain' | 'chart' | 'timeline' | 'documents';
  /**
   * PROD-2743 · the RULED encounter frame, DARK: the page passes this only when
   * `isEncounterFlagOn('frameV2')`. ABSENT → nothing below applies and the view renders exactly as
   * before (pinned by encounter-shell-view.frame-v2.test.tsx). PRESENT →
   *   · three-column mode: `leading` (the chart) on the leading edge for the whole encounter; the
   *     summary band, its fallback header and `topBar` are NOT passed to the frame;
   *   · `workspaceTabs` over the centre + right column; `activeTab` here (not the prop above) picks
   *     the centre body: the step canvas, the `chart` slot, or `documents`;
   *   · the step nav presented as the S·O·A·P ribbon (soap-ribbon.tsx), the A/P step carrying
   *     `pendingOrderCount`.
   */
  frameV2?: EncounterFrameV2;
  /** Steps that need attention (blank required items, or an A/P with no diagnosis), with up to three of what is missing:
   *  drawn on their cells in the step bar (`lib/step-completion.ts`). Absent → no marker. */
  stepAttention?: Record<string, string[]>;
  /** the blocks the practitioner passed ("Nothing to record") and the toggle. Display only. Absent → no pass UI. */
  stepPass?: StepPass;
  /** On frame v2 (the long page): the ribbon's ✓ and attention marker follow rule C (`lib/step-completion.ts`: the
   *  page's `completedSteps` and `stepAttention`, per section) instead of the section's documented-content rule; a Sign sits at
   *  the end of the page. Absent → frame v2 is exactly as before. */
  ruleC?: boolean;
  /** Long page: the steps of the section now in view (the page counts them as visited, for the attention marker). */
  onStepsVisited?: (steps: readonly PaStepKey[]) => void;
  /** slide the step bar in (once); `onStepBarRevealed` fires when it has. */
  stepBarReveal?: boolean;
  onStepBarRevealed?: () => void;
}

/** See `EncounterShellViewProps.frameV2`. */
export interface EncounterFrameV2 {
  /** The chart column content: the existing PatientSummaryRail, in `layout="chart"`. */
  leading: ReactNode;
  /**
   * The collapsed 72px strip's content (EncounterShell `leadingStrip`, D-41): the patient's
   * initials and the always-visible allergy dot. Absent → the strip holds only the expand control.
   */
  leadingStrip?: ReactNode;
  /**
   * PROD-2962: the collapsed Scrips Intelligence rail's content (EncounterShell `trailingRail`, D-41) —
   * the live Orb with the ambient status. Present → the right column is collapsible by the clinician's
   * chevron; it always starts OPEN (design desk D-67: D-62's NoteLine keeps labels beside values at the
   * ~660px card, so no start rule). Absent → unchanged.
   */
  intelligenceStrip?: ReactNode;

  /** The workspace tab strip (Encounter · Patient chart; the page header's tabs replace it on the long page). */
  workspaceTabs: ReactNode;
  activeTab: 'encounter' | 'terrain' | 'chart' | 'timeline' | 'documents';
  /** Orders staged but not sent (the page's existing order status). */
  pendingOrderCount?: number;
  /**
   * A panel open IN the centre column (CentrePanel: the generated note, Clinical edits, vitals
   * history). Present → it replaces the centre body whatever the tab; the chart column and the
   * right column never move (layout law: panels open in the centre, never over either rail).
   */
  centrePanel?: ReactNode;
  /** Counts the chart column's "Add to note" presses: each new value moves the one-scroll note to the Assessment (A).
   *  Zero or absent → nothing. One section at a time never reads it. */
  assessmentJump?: number;
  /** The clinician's saved-phrase library for the note's narratives (`.` and Macros). Absent → the
   *  narratives still work; no phrase is offered. */
  phrases?: StructuredNotePhrases;
  /** D-74 / D-45 (design desk, 6 Oct): the visit's vitals read state, for the Vitals block the note draws
   *  first in Objective when the template carries no vitals-block marker. Absent → ready. */
  vitals?: VitalsReadState;
  /** PROD-3040 · L1: what the visit's record holds, for template items filled from the chart. Absent → none filled. */
  chartContext?: ChartLaunchContext;
  /** PROD-3043 (D-73 amendment): "History notes", the chief-complaint comment, typed in the HPI block. */
  historyNote?: StructuredHistoryNote;
  /** PROD-2947 · 7 Oct 2026 ruling: the managed CDC STEADI / NRS lines Flutter writes into the Fall / Pain record, read
   *  back for the Risk block (read-only, when the template does not draw that screen) and the nurse's problems. */
  riskManaged?: RiskManaged;
  /** PROD-3128: the CDC STEADI follow-up under "Fall risk present" on a template that authors no STEADI. Absent → none. */
  riskSteadi?: QItem;
  /** PROD-3142 (D-78): the Fall / Pain list refused as stale, frozen until a reload. Absent → none. */
  riskFrozen?: { fall: boolean; pain: boolean };
  /**
   * What a PROVIDER sign freezes (a nurse-only sign freezes nothing). The page decides it from its sign state.
   *  · `answers`: the note's QuestionnaireResponse items (HPI, Risk, ROS, PE, narratives). The page passes `false`:
   *    a sign never locks them (Flutter sign model, 14 Oct 2026; QuestionBank records a snapshot per sign).
   *  · `records`: what is stored in the encounter record (Scrips.Patient): the chief complaint and the
   *    Fall / Pain assessments in the Risk canvas. Frozen after the provider sign; the server refuses
   *    those writes (409).
   * Absent (stories, tests): both follow `signed`.
   */
  signedFreeze?: { answers: boolean; records: boolean };
  /**
   * TS-3 (Samer, Loom 5 Oct): the note is ONE continuous scroll — the only mode clinicians see (design
   * desk D-64). `sections` (one section at a time) is for developers only: the page passes it when the
   * developer `noteSections` switch is on. Absent → one scroll.
   */
  noteMode?: NoteMode;
  /**
   * PROD-2961 (D1, option A): each note line's Delta with pieces, by linkId. The page keeps it and saves it
   * as the item's `cps-note-delta` extension beside the plain text. With `onNoteDeltaChange` wired, a
   * completed piece phrase is kept WITH its pieces (chosen codes, blank values, chart snapshots with their
   * source ids), a plain edit keeps every piece it does not touch, and a reload brings them back intact.
   */
  noteDeltas?: Readonly<Record<string, string>>;
  onNoteDeltaChange?: (linkId: string, delta: string | undefined) => void;
  /**
   * When the note was signed (the sign record's `createdDate`, a no-zone local stamp; the page's
   * `signLockSignedAt`). A signed note's header says "Signed <D-45 date, 24-hour time>" in place of a
   * draft's "Saved" (audit FV2-38; rule 38; R6c "Signed by …"). Absent → a signed note shows no save
   * line at all, never "Saved" and never an invented time.
   */
  signedAt?: string | null;
  /** Who signed (the provider's name), for the closing card's footer "Signed by <name> · <date, time>". Absent → the name is left out. */
  signedBy?: string | null;
  /**
   * PROD-2810 x TS-3 (integration of #626 and #606): the steps whose last save the server refused as
   * signed (the page's `refusedSteps`). A section holding one never shows ✓, whatever it documents: a
   * refused save is never "done". Absent → nothing refused.
   */
  refusedSteps?: readonly PaStepKey[];
}

/** The open S·O·A·P section's id and title, from the ribbon's own grouping (so the section heading
 *  and the ribbon tab can never disagree — the nurse's P reads "Nursing plan" in both). */
function sectionDefFor(nav: readonly PaStepKey[], current: PaStepKey): { id: SoapSectionId; title: string } {
  const id = nav.includes(current) ? sectionOf(nav, current) : sectionOf([current], current);
  const def = sectionsFor(nav).find((g) => g.id === id);
  return { id, title: def?.title ?? sectionsFor([current])[0]?.title ?? '' };
}

/** Announced (politely) when the page hands an order to P: the note moves, keyboard focus does not. */
export const ORDER_OPENED_IN_PLAN = 'Order opened in Plan';

/** The Scrips Intelligence launcher's name below 1280 (EncounterShell `noteLauncherLabel`, DS 2.20, D-41). */
export const NOTE_LAUNCHER_LABEL = 'Scrips Intelligence';

/** One scroll (TS-3): the frame's `step` while every section is on screen. The frame replays its canvas
 *  entry fade on every `step` change; scrolling between sections must not. */
const NOTE_SCROLL_STEP = 'note';

export function EncounterShellView(props: EncounterShellViewProps) {
  // Frame v2: the live response every block of the open section merges into (see StepCanvas).
  // Re-synced from the committed prop after every render; advanced synchronously by each merge.
  const sectionResponse = useRef(props.response);
  useEffect(() => {
    sectionResponse.current = props.response;
  }, [props.response]);
  // PROD-2961: the line Deltas, live — re-synced from the committed prop after every render and advanced
  // synchronously by each change, exactly like `sectionResponse`, so a burst of typing always rebases a
  // line's pieces onto the Delta that matches the text it is editing.
  const noteDeltasLive = useRef<Readonly<Record<string, string>>>(props.frameV2?.noteDeltas ?? {});
  useEffect(() => {
    noteDeltasLive.current = props.frameV2?.noteDeltas ?? {};
  }, [props.frameV2?.noteDeltas]);
  const onNoteDeltaChange = props.frameV2?.onNoteDeltaChange;
  const changeNoteDelta = useCallback(
    (linkId: string, delta: string | undefined) => {
      const live = noteDeltasLive.current;
      if (delta ? live[linkId] !== delta : linkId in live) {
        const next = { ...live };
        if (delta) next[linkId] = delta;
        else delete next[linkId];
        noteDeltasLive.current = next;
      }
      // Always told: the page's copy is the record, and the live copy may lag it (it is idempotent).
      onNoteDeltaChange?.(linkId, delta);
    },
    [onNoteDeltaChange],
  );
  const {
  patient,
  demographics,
  role,
  currentStep,
  completedSteps = [],
  onStepChange,
  questionnaire,
  stepTitles,
  hiddenSteps,
  saving,
  saved,
  saveError,
  status,
  visitInfo,
  onVisitDetails,
  patientSummary,
  summaryExpanded,
  onSummaryExpandedChange,
  drawer,
  topBar,
  virtualVisitNotice,
  growthChart,
  templateNotice,
  ambientCard,
  chart,
  documents,
  timeline,
  terrain,
  activeTab = 'encounter',
  frameV2,
  stepAttention,
  stepPass,
  ruleC,
  onStepsVisited,
  stepBarReveal,
  onStepBarRevealed,
  } = props;
  // #632 (PROD-3015, Ahmed): the Studio's sample category cards beside the HPI marker are canvas-only — the PA's
  // categories come per complaint from the server, inside the block. The OFF renderer strips them in StepCanvas; frame
  // v2's structured note reads the template directly, so it gets the same template without them (G8). OFF: unchanged.
  // Keyed on the switch (not the `frameV2` object, which a host may rebuild every render) and the template, so the note
  // keeps the same template object between renders — a new one drops the Risk block's Undo (structured-note-section).
  const frameV2On = !!frameV2;
  const noteQuestionnaire = useMemo(
    () => (frameV2On && questionnaire ? stripHpiSampleCategories(questionnaire) : questionnaire),
    [frameV2On, questionnaire],
  );
  // The section nav is resolved from the declarative block catalog (role-filtered) — DATA,
  // not a hardcoded list. The in-person host always resolves at modality 'in-person'.
  // [PROD-3376] The template's PE content decides only whether allied health gets the PE step.
  const steps = resolveSteps({
    modality: 'in-person',
    role: toClinicalRole(role),
    hiddenSteps,
    ...(toClinicalRole(role) === 'allied-health' ? { templateHasPeContent: stepHasContent(questionnaire, 'pe') } : {}),
  });
  // Step nav = the DS EncounterStepNav molecule — the Figma-recovered "Encounter step" strip
  // (E2 shell node 1937:182993, shipped in DS 1.50; PROD-1588 round 3 swap 2). It REPLACES the
  // interim Segmented pill strip: completion renders as the component's own IntakeSuccess check
  // badge (no composed Icon), and ALL steps stay on ONE row (the one-row ruling binds — the
  // two-row nav was reverted in #170; the <nav> scrolls horizontally when tight, never wraps).
  // EMPTY-SECTION TABS (author-driven nav): a questionnaire step with nothing authored under it
  // renders NO tab — the clinician never lands on a "configure in Template Studio" dead end. The
  // rule FAILS OPEN in five places (no/loading/errored questionnaire · unsectioned template ·
  // non-questionnaire steps · the step you are standing on · a would-be-empty nav) — see
  // `visibleSteps`. Hiding a clinically-needed section is worse than showing an inapplicable one.
  const navigableSteps = visibleSteps(steps, questionnaire, currentStep);
  // [PROD-3376] Allied health: the sections the template's coded groups add (the dietitian's Assessment; lib/allied-soap.ts).
  const alliedLayout = useMemo(() => alliedSoapLayout(questionnaire, toClinicalRole(role)), [questionnaire, role]);
  const extraSections = useMemo(() => alliedExtraSections(alliedLayout), [alliedLayout]);

  // ── TS-3 · one SOAP scroll (frame v2 only) ────────────────────────────────────────────────────
  // The note mode: one continuous scroll, the only mode clinicians see (D-64); one section at a time
  // only behind the developer switch. OFF (no `frameV2`) never reads it: every value below is inert there.
  const noteMode: NoteMode = frameV2?.noteMode ?? DEFAULT_NOTE_MODE;
  const noteOnScreen = !!frameV2 && frameV2.centrePanel == null && frameV2.activeTab === 'encounter';
  const scrollMode = !!frameV2 && noteMode === 'scroll';
  const { activeSection: sectionInViewId, jump: jumpToSection, bindRoot: bindNoteRoot } = useNoteScroll({
    enabled: scrollMode && noteOnScreen,
    nav: navigableSteps,
    currentStep,
    onStepChange,
  });
  // TS-3 · A and P (Samer A6): the ONE A/P step draws its assessment in A and its plan in P. In one
  // section at a time, which of the two is open while the step is the A/P is this view's own state;
  // the page's step stays 'ap' (one engine, one save) and only moves when the SECTION leaves A/P.
  // Standing on the A/P opens P, its navigation home (lib/soap-ribbon.ts): every place the page moves to
  // the A/P itself is about orders. The A tab opens the assessment (Codex P1, TS-3 PR 2 round 1).
  const [apPart, setApPart] = useState<ApPart>('plan');
  // Leaving the A/P (for S or O) puts its part back to P, so the next arrival without a tab — an order
  // set applied from the drawer moves the page to the A/P with no seed — opens P, where the orders land
  // (Codex P1, round 2). Adjusted during render on the step change, never in an effect.
  const [stepBefore, setStepBefore] = useState(currentStep);
  if (currentStep !== stepBefore) {
    setStepBefore(currentStep);
    if (stepBefore === 'ap' && currentStep !== 'ap' && currentStep !== 'wrap-up') setApPart('plan');
  }
  const openSectionId: SoapSectionId | undefined = !navigableSteps.includes(currentStep)
    ? undefined
    : currentStep === 'ap'
      ? apPart === 'assessment'
        ? 'a'
        : 'plan'
      : sectionOf(navigableSteps, currentStep);
  /** One section at a time: a tab opens its section (A or P picks the A/P's part). */
  const selectSection = (id: SoapSectionId) => {
    const section = sectionsFor(navigableSteps, extraSections).find((g) => g.id === id);
    if (!section) return;
    const open = () => {
      if (section.apPart) setApPart(section.apPart);
      if (!section.items.includes(currentStep) && section.items[0]) onStepChange(section.items[0]);
    };
    // One section at a time: another section replaces the open one (A and P too, which share the A/P step), so an
    // open, filled phrase is asked about first (#689 follow-up). The step change inside a confirmed change does not ask
    // again. One scroll keeps every section on screen, so it never asks (its ribbon jumps instead; kept explicit here).
    if (scrollMode || id === openSectionId) open();
    else confirmNoteViewChange(open);
  };
  // An order handed to the A/P from elsewhere (Add Order, an ambient or order-set order) is finished in
  // its PLAN part, so P is where the note goes: one scroll jumps there, one section at a time opens it.
  const seedRequest = props.assessmentPlan?.seedRequest;
  // One section at a time: open P as the new order arrives — adjusted during render on the prop change
  // (React's documented alternative to an effect that sets state).
  const [seedFor, setSeedFor] = useState(seedRequest);
  // The page moves the note to P without moving keyboard focus (the clinician may be mid-entry), so a
  // polite live announcement says where the order went (design desk, 5 Oct). It opens there; it is not
  // added until its form is saved, hence "opened".
  const [announcement, setAnnouncement] = useState('');
  if (seedRequest !== seedFor) {
    setSeedFor(seedRequest);
    if (frameV2 && seedRequest && !scrollMode) setApPart('plan');
    if (frameV2 && seedRequest) setAnnouncement(ORDER_OPENED_IN_PLAN);
  }
  // One scroll: move the note (the DOM) to P once per new order.
  const seedJumped = useRef<unknown>(null);
  useEffect(() => {
    if (!scrollMode || !seedRequest || seedJumped.current === seedRequest) return;
    seedJumped.current = seedRequest;
    // The page moved the note, not the clinician: focus stays where it is (the order's form takes it).
    jumpToSection('plan', { focus: false });
  }, [scrollMode, seedRequest, jumpToSection]);
  // The chart column's "Add to note" put a problem in the Assessment: one scroll moves the note to A. The press
  // is the clinician's own, but the click was in the chart column, so keyboard focus stays there.
  const assessmentJump = frameV2?.assessmentJump ?? 0;
  const assessmentJumped = useRef(0);
  useEffect(() => {
    if (!scrollMode || !assessmentJump || assessmentJumped.current === assessmentJump) return;
    assessmentJumped.current = assessmentJump;
    jumpToSection('a', { focus: false });
  }, [scrollMode, assessmentJump, jumpToSection]);
  // A section's ✓ = documented and no open required blank (structured-note/section-completion.ts);
  // never a visit. Computed only with the switch on: OFF keeps `completedSteps` exactly as before.
  const RULE_C_STEPS: readonly PaStepKey[] = ['hpi', 'risk', 'ros', 'pe', 'ap'];
  const ruleCOn = !!frameV2 && !!ruleC;
  // Rule C on the ribbon: a section shows ✓ when it holds a step that can be ✓ and every such step is (derived from the note).
  const ruleCSections = ruleCOn
    ? new Set(
        sectionsFor(navigableSteps, extraSections)
          .filter((g) => {
            const capable = g.items.filter((i) => RULE_C_STEPS.includes(i));
            return capable.length > 0 && capable.every((i) => completedSteps.includes(i));
          })
          .map((g) => g.id),
      )
    : undefined;
  const attentionSections: Partial<Record<SoapSectionId, string[]>> | undefined =
    ruleCOn && stepAttention
      ? Object.fromEntries(
          sectionsFor(navigableSteps, extraSections)
            .map((g) => [g.id, g.items.flatMap((i) => stepAttention[i] ?? []).slice(0, 3)] as const)
            .filter(([, m]) => m.length > 0),
        )
      : undefined;
  // a ribbon section reads "Passed" when it is not ✓, holds a passable block and every passable block in it is passed
  // or ✓ (at least one passed). Display only.
  const passedSections: ReadonlySet<SoapSectionId> | undefined =
    ruleCOn && stepPass
      ? new Set(
          sectionsFor(navigableSteps, extraSections)
            .filter((g) => {
              const passable = g.items.filter((i) => isPassableStep(i));
              return (
                passable.some((i) => stepPass.passed.has(i)) &&
                passable.every((i) => stepPass.passed.has(i) || completedSteps.includes(i)) &&
                !ruleCSections?.has(g.id)
              );
            })
            .map((g) => g.id),
        )
      : undefined;
  // One scroll: a section in view counts as visited (rule C's attention marker needs a visit or an answer).
  const inViewItems = ruleCOn && scrollMode && sectionInViewId ? sectionsFor(navigableSteps, extraSections).find((g) => g.id === sectionInViewId)?.items : undefined;
  const inViewKey = inViewItems?.join(' ');
  useEffect(() => {
    if (inViewItems && onStepsVisited) onStepsVisited(inViewItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inViewKey]);
  // The long page's "n/N" beside each block title: this block's place among the page's blocks, set on the DS title from the DOM
  // (the DS owns the block markup), so the bridge draws it. Re-set whenever the blocks change.
  const blockCountKey = ruleCOn ? navigableSteps.join(',') : '';
  useEffect(() => {
    if (!ruleCOn) return;
    const titles = document.querySelectorAll<HTMLElement>('[data-long-page] section[id^="note-block-"] > header > h3');
    titles.forEach((h, i) => {
      h.setAttribute('data-step-n', String(i + 1));
      h.setAttribute('data-step-of', String(titles.length));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockCountKey, ruleCOn, props.response, props.assessmentPlan]);
  const documentedSections = ruleCSections
    ? ruleCSections
    : frameV2
    ? completedSections({
        nav: navigableSteps,
        questionnaire: noteQuestionnaire,
        response: props.response,
        plan: props.assessmentPlan
          ? {
              diagnoses: props.assessmentPlan.diagnoses,
              orders: props.assessmentPlan.orders,
              articles: props.assessmentPlan.articles,
              assessmentNote: props.assessmentPlan.assessmentNote,
            }
          : undefined,
      })
    : undefined;
  // PROD-2810: a section that holds a step whose save was refused as signed shows no ✓ (#626's rule, kept
  // in #606's documented-content completion).
  const refused = frameV2?.refusedSteps;
  const doneSections =
    documentedSections && refused && refused.length > 0
      ? new Set(
          [...documentedSections].filter(
            (id) => !sectionsFor(navigableSteps, extraSections).some((g) => g.id === id && g.items.some((step) => refused.includes(step))),
          ),
        )
      : documentedSections;
  const navSteps: EncounterStepNavStep[] = navigableSteps.map((step) => ({
    id: step,
    // AUTHORED TITLE (cps-layout `stepTitles`) for HPI/ROS/PE/Risk; built-in label otherwise or
    // whenever the authored value is absent/blank. Rendered as text — never as HTML.
    label: stepLabel(step, stepTitles),
    completed: completedSteps.includes(step),
  }));
  // Step progress (shell.spec.json Frame 1565, DS 1.50 ProgressBar atom): the 2px full-width
  // status-success run under the step-header row. Progress = completed steps over resolved steps.
  /*
   * Band disclosure — the port of Flutter's `isPatientInfoViewExpanded`
   * (encounter_screen.dart:113). ONE flag drives TWO things there, and does here too:
   *   height  — :1112-1136, band `ConstrainedBox` 180px collapsed / `initialEditorHeight * .7`
   *             expanded, with the canvas taking the remainder (.3 when expanded);
   *   content — :1449-1454, `ClinicalInfoViewType.view` -> `.editable`, i.e. inline summary
   *             lists become per-entry rows with add/delete/resolve.
   * The DS `EncounterShell` frame owns the height half; the page forwards `summaryExpanded` to PatientSummaryRail
   * for the content half, so the chevron is the single control Flutter's is.
   * The frame falls back to uncontrolled when the host passes no `summaryExpanded`.
   */
  /* Attribute-section disclosure (design lines 375-381). Local UI state only — nothing to
     persist: collapsing a section hides questions, it does not change the response. Resets per
     step because the key is the step itself. */
  // (The step-level HPI disclosure state was removed with the disclosure itself — Ahmed
  // 2026-09-12. Collapsing lives only on each complaint's wrapper now.)
  // The visit strip (and the frame's strip | nav divider) renders only when a cell resolves.
  // Frame v2 writes the visit time in the D-45 format, 24-hour (audit FV2-25); OFF keeps today's label.
  const cells = visitInfoCells(visitInfo, { d45: !!frameV2 });
  const hasVisitInfo = cells.length > 0;
  const visitStripNode = hasVisitInfo ? (
    /* The drill chevron is passed ONLY when the host wired a real handler (design line 305
       "Visit details"); unwired hosts keep the pre-design behaviour — no chevron. */
    <VisitInfoStrip
      cells={cells}
      onDrill={onVisitDetails}
      drillLabel="Visit details"
      // Frame v2 (signal-ds 2.20, FV2-02): the cell labels in text-secondary (AA). OFF: no key.
      {...(frameV2 ? { labelTone: 'secondary' as const } : {})}
    />
  ) : undefined;
  const saveState: EncounterShellSaveState = { error: saveError, saving, saved, status };

  // Progress is measured over the steps the clinician can actually REACH (the nav's own list):
  // counting a hidden, unreachable step in the denominator would cap the bar below 100% forever.
  const progressPct =
    navigableSteps.length > 0
      ? Math.round(
          (navigableSteps.filter((s) => completedSteps.includes(s)).length / navigableSteps.length) * 100,
        )
      : 0;
  // Frame v2: progress is documented SECTIONS over sections, the same answer the track's ✓ gives.
  const sectionCount = frameV2 ? sectionsFor(navigableSteps, extraSections).length : 0;
  const frameV2Progress = doneSections && sectionCount > 0 ? Math.round((doneSections.size / sectionCount) * 100) : 0;

  // PROD-1986 — Chart tab active: swap the step chrome + canvas for the chart body, keeping the
  // top bar, the summary band and the right drawer mounted. Gated on the slot so Storybook /
  // no-chart hosts are untouched.
  const showChart = activeTab === 'chart' && !!chart;
  const showDocuments = activeTab === 'documents' && !!documents;
  const showTimeline = activeTab === 'timeline' && !!timeline;
  const showTerrain = activeTab === 'terrain' && !!terrain;

  // PROD-2743 frame v2 (DARK). Everything the frame does differently is keyed on `frameV2` being
  // present; when it is absent every value below equals what the frame was handed before.
  //   · the centre body: in v2 the WORKSPACE tab decides (the frame's `chart` slot hosts both the
  //     Patient chart and the Documents — each replaces the header row + step canvas);
  //   · three-column props only in v2, so the default render is handed nothing new.
  const centreSwap: ReactNode = frameV2
    ? frameV2.centrePanel != null
      ? frameV2.centrePanel
      : frameV2.activeTab === 'chart'
        ? chart
        : frameV2.activeTab === 'terrain'
          ? terrain
        : frameV2.activeTab === 'timeline'
          ? timeline
          : frameV2.activeTab === 'documents'
            ? documents
            : undefined
    : showChart
      ? chart
      : showTerrain
        ? terrain
      : showTimeline
        ? timeline
        : showDocuments
          ? documents
          : undefined;
  // D-68 (design desk, 6 Oct; audit FV2-39): the patient's identity must be on screen at every width,
  // so this host knows whether the chart card is visible and tells the compact identity line
  // (frame-v2-patient-identity.tsx) through `ChartCardHiddenContext`. The chart column's fold is the
  // prototype's (left-panel-fold.tsx, 15 Oct): the column takes no room folded, one toggle on the header
  // line, folded below 1280 until the practitioner opens it (it was folded below 1384 by the DS rule).
  // The DS frame is never handed `leadingCollapsed`: the width is the animated `--enc-left-width`, so the
  // column slides instead of swapping to a 72px strip. Frame v2 only.
  const leftPanel = useLeftPanelFold();
  // `?x=intel-always-open` (Antonin's zoning, encounter-zoning.ts): both side columns open on arrival on every tab, folded only
  // for the 480 floor (nav rail first, then Intelligence as an overlay; the chart and Intelligence are independent, lock §2 amended); the clinician's choices hold for the page session. It
  // replaces D-41's one-panel-at-a-time below. Frame v2 only; OFF (null) leaves every value below as it was.
  // The zoning's overlay host lives in the long page only, so the zoning runs only there (Codex, fifth pass).
  const [zoningOn] = useState(() => !!frameV2 && !!ruleC && isZoningOn());
  const zoning = useEncounterZoning(zoningOn);
  const chartCollapsed = zoning ? !zoning.contextShown : leftPanel.folded;
  // D-41 (amended, desk on signal-ds #454; DS 2.20): below 1280 Scrips Intelligence opens as a 380 panel that
  // narrows the note (never overlays), and ONE side panel is open at a time — opening Intelligence collapses
  // the chart to its strip, expanding the chart closes Intelligence. From 1280 up `trailingOpen` is inert
  // (the column is inline), so neither move changes anything there. Frame v2 only.
  const [intelligenceOpenLocal, setIntelligenceOpen] = useState(false);
  // The long page (ruleC): Scrips Intelligence is the floating round button bottom right when collapsed (IntelligenceFab, drawn
  // from the DS's collapsed strip by the bridge), which opens it.
  // `?x=intel-close` (PROD-3388, Antonin 3 Nov: "keep it visible everywhere, never collapsed. It also needs a close"; ON on the
  // dev hosts): it starts OPEN, and the X in its header closes it (rail-close.ts); the DS column's own chevron is not drawn (the
  // bridge, under `data-intel-close`), so the rail has one close, the same as on the pre-visit. Off: it starts collapsed and the
  // DS chevron closes it, as before. Read once per mount.
  const [intelCloseOn] = useState(() => isEncounterFlagOn('intelClose'));
  // `?x=intel-rail-catalog`: Antonin's catalog rail; its close and reopen are the ones above (`?x=intel-close`).
  const [catalogRail] = useState(() => isEncounterFlagOn('intelRailCatalog'));
  const [intelCollapsedLocal, setIntelCollapsedLocal] = useState(!intelCloseOn);
  // `?x=intel-always-open` (the zoning): Scrips Intelligence is open on arrival (an overlay under the 480 floor), and the X, the DS
  // chevron, the launcher and the orb button are the clinician's choice (`choosePanel`), held for the page session.
  // Docked only: below 1280 the DS's in-track panel pushes the centre, so a panel under the 480 floor (the zoning's
  // overlay) is not handed to it; it is drawn over the centre below (`intel-overlay`), as on the pre-visit.
  const intelligenceOpen = zoning ? zoning.panelOpen : intelligenceOpenLocal;
  const intelOverlay = !!zoning?.panelOverlay;
  const intelCollapsed = zoning ? !zoning.panelShown : intelCollapsedLocal;
  const setIntelCollapsed = (collapsed: boolean) => (zoning ? choosePanel(!collapsed) : setIntelCollapsedLocal(collapsed));
  const intelFabRef = useRef<HTMLButtonElement>(null);
  // From 1280 up the X folds the inline column to the orb button. Below 1280 the rail is D-41's panel, opened from the DS
  // launcher: the X closes that panel and the launcher reopens it (no orb button beside the launcher).
  const longPageRef = useRef<HTMLDivElement>(null);
  const focusLauncher = useRef(false);
  const closeIntel = zoning
    ? () => {
        // The DS launcher takes focus back only from its own docked panel; the overlay hands it to the orb button (below).
        if (window.innerWidth < 1280 && zoning.panelOpen) focusLauncher.current = true;
        choosePanel(false);
      }
    : intelCloseOn
    ? () => {
        if (window.innerWidth >= 1280) setIntelCollapsed(true);
        else {
          focusLauncher.current = true;
          setIntelligenceOpen(false);
        }
      }
    : undefined;
  // The X goes with the rail: focus moves to the button that reopens it (the orb button; below 1280 the DS launcher, named
  // "Scrips Intelligence"), never to the page body. Switch off: the DS chevron keeps its own focus.
  const intelWasCollapsed = useRef(intelCollapsed);
  useEffect(() => {
    // With the zoning, only a Close moves focus (a fold for the floor, or the chart taking the room, leaves it where it is).
    const closedByChoice = zoning ? zoning.panelClosed : intelCloseOn;
    if (closedByChoice && intelCollapsed && !intelWasCollapsed.current) intelFabRef.current?.focus();
    intelWasCollapsed.current = intelCollapsed;
  });
  // The overlay (the zoning under the 480 floor) keeps the dialog behaviour the DS dock's `manageFocus` gave the panel it
  // replaces (Codex on #931): opened from the orb button, focus moves into it (the button that opened it is gone); Escape
  // closes it (unless a layer inside handled Escape), and the Close path above puts focus back on the orb button.
  const overlayRef = useRef<HTMLDivElement>(null);
  const focusOverlay = useRef(false);
  // The overlay starts below the virtual-visit notice at the top of the centre (its Connect / Leave stay clickable; Codex,
  // sixth pass): measured every render, set as --intel-host-top on the host (ledgered runtime property). Docked, the host
  // lies over its own track, beside the notice, so it starts at the top.
  useLayoutEffect(() => {
    const host = overlayRef.current;
    const row = host?.parentElement;
    if (!host || !row) return;
    const notice = intelOverlay ? row.querySelector<HTMLElement>('[data-slot="enc-visit-notice"]') : null;
    const top = notice ? Math.max(0, Math.round(notice.getBoundingClientRect().bottom - row.getBoundingClientRect().top)) : 0;
    host.style.setProperty('--intel-host-top', `${top}px`);
    // The DS's in-track panel under the host (docked below 1280) only reserves the width: its Done button stays out of the
    // tab order and the accessibility tree while the zoning owns the rail (Codex, seventh pass).
    row.querySelectorAll<HTMLElement>('[data-slot="encounter-note-panel-track"]').forEach((el) => el.setAttribute('inert', ''));
  });
  const openIntel = () => {
    if (zoning) focusOverlay.current = true;
    setIntelCollapsed(false);
  };
  // The flag is consumed by the open it was armed for: an overlay open takes focus in; a docked open just clears it, so a
  // later resize into the overlay never steals focus (Codex, fifth pass).
  const intelShown = !!zoning?.panelShown;
  useEffect(() => {
    if (!intelShown || !focusOverlay.current) return;
    focusOverlay.current = false;
    // Docked or overlay, the orb button that opened it is gone: focus goes into the rail (Codex, seventh pass).
    overlayRef.current?.querySelector<HTMLElement>('button:not([disabled]), [href], input, textarea, select, [tabindex]:not([tabindex="-1"])')?.focus();
  }, [intelShown, intelOverlay]);
  useEffect(() => {
    if (!intelOverlay) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      choosePanel(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [intelOverlay]);
  useEffect(() => {
    if (intelligenceOpen || !focusLauncher.current) return;
    focusLauncher.current = false;
    longPageRef.current?.querySelector<HTMLButtonElement>(`button[aria-label="${NOTE_LAUNCHER_LABEL}"]`)?.focus();
  }, [intelligenceOpen]);
  // The one look for what opens OVER the long page (design EncOverlayScope, 21-10): <body> carries `data-enc-overlay` while the
  // long page is mounted (encounter-overlay-look.css hangs on it). Look only; the legacy frame never sets it.
  useEncOverlayScope(ruleCOn);
  const onIntelligenceOpenChange = (open: boolean) => {
    // The DS launcher below 1280: an open goes through the orb's handoff (focus into the overlay), a close through Close.
    if (zoning) return open ? openIntel() : choosePanel(false);
    setIntelligenceOpen(open);
    if (open) leftPanel.setFolded(true);
  };
  const onChartCollapsedChange = (collapsed: boolean) => {
    if (zoning) return chooseContext(!collapsed);
    leftPanel.setFolded(collapsed);
    if (!collapsed) setIntelligenceOpen(false);
  };

  // DS 2.14.0 types the three-column props (scrips-signal-ds #424). Spread, not passed as
  // `leading={…}`: OFF hands the frame no `leading` / `workspaceTabs` key at all, so the default
  // frame keeps exactly the props it had (pinned by the OFF prop-key test).
  const threeColumnProps: Pick<
    EncounterShellProps,
    | 'leading'
    | 'workspaceTabs'
    | 'leadingStrip'
    | 'leadingCollapsed'
    | 'onLeadingCollapsedChange'
    | 'trailingCollapsed'
    | 'onTrailingCollapsedChange'
    | 'trailingRail'
    | 'noteLauncherLabel'
    | 'trailingOpen'
    | 'onTrailingOpenChange'
  > = frameV2
    ? {
        leading: frameV2.leading,
        // The strip's tab row carries the left panel's one toggle, on its bottom hairline (left-panel-fold.tsx);
        // `leadingStrip` (the allergy dot) rides the folded pill, since the DS strip is no longer drawn.
        // Long page (ruleC): the app's one page header (`topBar`) replaces the workspace tabs, and carries the toggle.
        workspaceTabs: ruleCOn ? undefined : (
          <>
            {frameV2.workspaceTabs}
            <LeftPanelToggle
              placement="tabs"
              folded={chartCollapsed}
              onToggle={() => onChartCollapsedChange(!chartCollapsed)}
              indicator={frameV2.leadingStrip}
            />
          </>
        ),
        ...(frameV2.intelligenceStrip != null
          ? { trailingRail: frameV2.intelligenceStrip }
          : {}),
        leadingCollapsed: false,
        noteLauncherLabel: NOTE_LAUNCHER_LABEL,
        trailingOpen: intelligenceOpen,
        onTrailingOpenChange: onIntelligenceOpenChange,
        ...(ruleCOn ? { trailingCollapsed: intelCollapsed, onTrailingCollapsedChange: setIntelCollapsed } : {}),
      }
    : {};

  // FV2-05 / FV2-38 · the note header (R6c Encounter Frame: the role, then a labelled "Sign note"
  // button, always on screen while the note scrolls). The role is the signed-in clinician's (the same
  // label every block's author seat uses); once their sign is recorded it reads "· Signed" and the
  // button goes, as R6c's "Nurse · Signed" does. A PROVIDER sign freezes the note (rule 38).
  const signLock = props.wrapUp?.signLock;
  const providerFrozen = frameV2?.signedFreeze?.records ?? !!props.signed;
  // A nurse's OWN sign is recorded (a sign record and no provider sign yet: once a provider has signed,
  // the record cannot tell this nurse's sign from the provider's, so the chip never claims it — Codex P2).
  const nurseSigned = role === 'nurse' && !!signLock && signLock.state !== 'draft' && !providerFrozen;
  // The viewer's role chip says "· Signed" only for the viewer's own kind of sign: a provider sign for a
  // provider, the nurse's sign for a nurse.
  const chipSigned = role === 'nurse' ? nurseSigned : providerFrozen;
  // The note is past drafting for this viewer: no Sign button, and never a draft's "Saved".
  const ownSignDone = providerFrozen || nurseSigned;
  const roleLabel = authorRoleLabel(toClinicalRole(role));
  const roleChip = roleLabel ? (chipSigned ? `${roleLabel} · Signed` : roleLabel) : undefined;
  // The tooltip uses the header button's own words: "Sign note" / "Sign as nurse" (PROD-3031 nit).
  const signGate = signActionState(signLock, role === 'nurse' ? 'Sign as nurse' : 'Sign note');
  const headerSign =
    frameV2 && signLock && !ownSignDone
      ? {
          // Structured Note v2 names the nurse's sign for what it is; everyone else signs the note.
          label: role === 'nurse' ? 'Sign as nurse' : 'Sign note',
          disabled: signGate.disabled,
          title: signGate.title,
          onSign: signLock.onSign,
          reason: signLock.refusedReason,
        }
      : undefined;
  // The long page's closing card ends with a foot (the Sign, or after the provider sign who and when). It also carries the note
  // actions and Send orders as labelled buttons (22 Oct review, NoteSignActions), which the Wrap-up bar then leaves out.
  const footOn = ruleCOn && (!!headerSign || providerFrozen);
  // `visitNoteV2` (Samer's Loom, 3 Nov: "the type of visit disappeared"): one read-only line at the top of the note, the served
  // template's name and the visit's type. Display only; the long page drew neither (the template pill is the step frame's).
  const noteV2 = useVisitNoteV2();
  const visitTypeLabel = props.visitType === 'follow-up' ? 'Follow-up visit' : props.visitType === 'first' ? 'First visit' : undefined;
  const templateRow = noteV2 ? [props.templateName, visitTypeLabel].filter(Boolean).join(' · ') : '';
  // List A9 (`visitNoteV2`): a floating bar lists what is still needed before signing (the note's required blanks, the same
  // list as "Before you sign"), or "Ready to sign" with a jump to the Sign. The note keeps ONE Sign, in the foot, untouched
  // (ruling 3 Nov, PROD-3419: the Sign closes the Wrap-up card, #897).
  const signBar = noteV2 && footOn && !!headerSign && !providerFrozen;
  const goToFootSign = () => {
    const sign = document.querySelector<HTMLElement>('[data-slot="note-end-sign"] [data-slot="note-sign-button"]');
    if (!sign) return;
    scrollJumpTarget(sign);
    sign.focus({ preventScroll: true });
  };
  const signBlankItems = signBar
    ? signBlanks(noteQuestionnaire, navigableSteps, props.response).map((b) => ({ id: b.linkId, label: b.label, onSelect: () => goToSignBlank(b, onStepChange) }))
    : [];

  /** One S·O·A·P section of the structured note — the same component in either note mode (frame v2). */
  const renderNoteSection = (
    section: { id: SoapSectionId; title: string },
    sectionSteps: readonly PaStepKey[],
    part?: ApPart,
  ) => (
    <StepPassContext.Provider value={ruleCOn ? stepPass : undefined}>
    <InsertedTemplatesSlot>
    {(inserts) => (
    <StructuredNoteSection
      {...inserts}
      section={section}
      steps={sectionSteps}
      wrapUpCard={ruleCOn}
      apPart={part}
      noteDeltas={frameV2?.noteDeltas}
      noteDeltasLive={noteDeltasLive}
      onNoteDeltaChange={onNoteDeltaChange ? changeNoteDelta : undefined}
      navigableSteps={navigableSteps}
      role={role}
      signed={props.signed}
      questionnaire={noteQuestionnaire}
      stepTitles={stepTitles}
      response={props.response}
      sharedResponseRef={sectionResponse}
      onResponseChange={props.onResponseChange}
      onStepChange={onStepChange}
      resolveChiefComplaint={props.resolveChiefComplaint}
      resolveVitals={props.resolveVitals}
      vitalsStatus={frameV2?.vitals}
      chartContext={frameV2?.chartContext}
      chiefComplaints={props.chiefComplaints}
      phrases={frameV2?.phrases}
      historyNote={frameV2?.historyNote}
      riskManaged={frameV2?.riskManaged}
      riskSteadi={frameV2?.riskSteadi}
      riskFrozen={frameV2?.riskFrozen}
      signedFreeze={frameV2?.signedFreeze}
      riskReady={props.riskReady}
      riskReadFailed={props.riskReadFailed}
      patientSex={props.demographics?.sex}
      renderStepCanvas={(step, { bare, questionnaire: only, frozen, apPart: drawPart }) => (
        // `only`: the Risk canvas below its block draws just what the block does not. `drawPart`: the
        // A/P's assessment (A) or plan (P) part. `frozen` (PROD-2810): a provider sign freezes the
        // canvas in both note modes.
        <StepCanvas
          {...props}
          // The long page's foot draws the note actions and Send orders (22 Oct review): the Wrap-up bar leaves them out.
          {...(footOn && props.wrapUp
            ? { wrapUp: { ...props.wrapUp, footActions: true, footSendOrders: !!footSendOrdersAction(props.wrapUp) } }
            : {})}
          questionnaire={only ?? props.questionnaire}
          currentStep={step}
          sharedResponse={sectionResponse}
          bare={bare}
          frozen={frozen}
          apPart={drawPart}
          inNote
        />
      )}
    />
    )}
    </InsertedTemplatesSlot>
    </StepPassContext.Provider>
  );

  // The templates added from "/" sit right after the note's own template sections (S, O). Each one is its own answer set.
  const insertedRole = role === 'nurse' ? 'nurse' : 'physician';
  const insertedPanel = (className?: string) => <InsertedTemplatesPanel role={insertedRole} className={className} />;
  const holdsTemplate = (items: readonly PaStepKey[]) => items.some(isQuestionnaireStep);

  /** One section at a time: the open section (A or P for the A/P step); a step the nav does not hold
   *  stands alone, as before. */
  const renderOpenSection = () => {
    const open = openSectionId ? sectionsFor(navigableSteps, extraSections).find((g) => g.id === openSectionId) : undefined;
    const items = open ? open.items : sectionStepsFor(navigableSteps, currentStep);
    const section = open
      ? renderNoteSection({ id: open.id, title: open.title }, open.items, open.apPart)
      : renderNoteSection(sectionDefFor(navigableSteps, currentStep), items);
    return holdsTemplate(items) ? (
      <>
        {section}
        {insertedPanel('mt-6')}
      </>
    ) : (
      section
    );
  };

  // One rule for the foot's Send orders and the bar's `footSendOrders` (lib/foot-send-orders.ts).
  const footSendOrders = footOn ? footSendOrdersAction(props.wrapUp) : undefined;
  const signedStamp = frameV2?.signedAt ? parseNoZIsoDatetime(frameV2.signedAt) : null;
  const signedBy = frameV2?.signedBy?.trim() || undefined;
  const signedLine = ownSignDone && signedStamp ? `Signed ${formatDateTimeD45(signedStamp)}` : undefined;

  // ── CHIEF COMPLAINT — PROD-2577 part B ──────────────────────────────────────────────────────
  // The editor field (create) + the picked-complaint cards (remove) used to be a PA-drawn
  // surface mounted beside the DS `ChiefComplaintHpiBlock`. Ahmed 2026-09-24: "the HPI tab
  // renders entirely from the template, so the practitioner app no longer composes the field
  // beside it" — the picker (search, pencil free-text) now draws INSIDE the block, and the
  // remove glyph sits on each complaint's own wrapper, so one complaint is one card. See
  // `resolveChiefComplaint`'s `onAdd`/`onSearch`/`onRemove`/`pickerLabel`/`pickerPlaceholder` on
  // the page. This host no longer holds a second, PA-drawn copy of the field or the cards.

  // ── STEP BAR (Antonin, 15 Oct: "the continue button should be one container under, with a stepper like in the
  // prototype. No button near tabs in header") ──────────────────────────────────────────────────────────────────
  // With the page header above it (`topBar`), the default frame's stepper leaves the row under the summary band and
  // sits in the step bar directly under the header, with its progress line and the page's primary action, Next step
  // (the same `onStepChange` a step click calls, so the same autosave flush). The last step has no Next: the note's
  // Sign stays where Antonin approved it (the rail). The Chart tab has no steps, so no step bar. Frame v2 is untouched:
  // its own step header (ribbon + Sign) already sits under its workspace tabs. No `topBar` (stories, no-data paths):
  // the stepper stays in the frame's row as before.
  const stepBarOwnsNav = !frameV2 && topBar != null;
  // Long page: the page's one header  with the left panel's toggle on its bottom edge, in place of
  // frame v2's workspace tab strip. Frame v2 alone passes the frame no top bar, as before.
  const headerBar =
    ruleCOn && isValidElement(topBar)
      ? cloneElement(topBar as React.ReactElement<{ leftToggle?: ReactNode }>, {
          leftToggle: (
            <LeftPanelToggle
              placement="header"
              folded={chartCollapsed}
              onToggle={() => onChartCollapsedChange(!chartCollapsed)}
              indicator={frameV2?.leadingStrip}
            />
          ),
        })
      : undefined;
  const nextStep = nextStepOf(navigableSteps, currentStep);
  const stepBar =
    stepBarOwnsNav && activeTab === 'encounter' ? (
      <EncounterStepBar
        steps={navSteps}
        activeId={currentStep}
        onStepChange={(id) => onStepChange(id as PaStepKey)}
        progress={progressPct}
        reveal={stepBarReveal}
        onRevealed={onStepBarRevealed}
        attention={stepAttention}
        actions={
          nextStep ? (
            <Button size="sm" onClick={() => onStepChange(nextStep)}>
              Next
            </Button>
          ) : signLock && !ownSignDone ? (
            /* The last step's finishing action (Antonin, 16 Oct: "Wrap up still needs a CTA at the end"): the note's own
               Sign, where Next sits on the other steps. Same handler (`signLock.onSign`, the page's handleSign with its
               required-items, diagnosis and save gates) and the same disabled state and reason as the header's Sign in frame v2
               and the rail's. Gone once this viewer's own sign is recorded. */
            <Button
              size="sm"
              data-slot="step-bar-sign"
              disabled={signGate.disabled}
              title={signGate.disabled ? signGate.title : undefined}
              onClick={signLock.onSign}
            >
              {role === 'nurse' ? 'Sign as nurse' : 'Sign note'}
            </Button>
          ) : undefined
        }
      />
    ) : undefined;

  // The signed-note strip under the section bar (design EncLockedNotice, 21-10): once the PROVIDER has signed, or a nurse's own sign
  // is recorded, the long page says so at its top with the same status line the closing card's footer carries. No Amend note
  // action: the PA has no amend path (the Flutter sign model, a sign never locks the note; the note stays editable).
  const lockedNotice =
    ruleCOn && (providerFrozen || nurseSigned) ? (
      <Banner
        data-slot="note-locked-notice"
        variant={providerFrozen ? 'info' : 'warning'}
        title={providerFrozen ? 'This note is signed' : 'Nurse signed, waiting for the physician'}
      >
        <NoteStatusLine
          orderSent={props.wrapUp?.status?.orderSent}
          showOrders={!!props.wrapUp?.status && !props.wrapUp?.signLock?.isNurse}
          ePrescription={props.wrapUp?.ePrescription?.visible ? { synced: !!props.wrapUp.ePrescription.synced } : undefined}
          providerSigned={providerFrozen}
          signedBy={signedBy}
          signedWhen={signedStamp ? formatDateTimeD45(signedStamp) : undefined}
        />
      </Banner>
    ) : undefined;

  const shell = (
    /* The started-encounter FRAME is the Signal DS `EncounterShell` (graduated from this file,
       2026-09-23): main column (top bar · notice · summary band + chevron · visit strip | step
       nav | save state · progress · canvas) beside the generated-note dock. The frame owns the
       geometry, the band clamp, the single scroll region and the entry motion; this host keeps
       the hooks and decides WHICH step body goes in the canvas. Flutter:
       encounter_screen.dart:1085-1206. PROD-1986 Chart tab: `chart` swaps the header + canvas for
       the chart body with the band pinned collapsed. */
    <EncounterShell
      /* One scroll (TS-3): every section is on screen, so a step change as the clinician scrolls must not
         replay the canvas entry fade (the frame replays it on every `step` change). */
      step={scrollMode ? NOTE_SCROLL_STEP : currentStep}
      /* Every step sits on the white card surface — the deployed Flutter app draws each section
         on white (dev-provider, 2026-09-26; Ahmed: "all tabs … white not gray"). Frame v2: the note's
         blocks are white cards, so the canvas under them is the page ground (sheet §1 Surfaces: "page
         on canvas; cards lift" — never a flat white card on white; audit FV2-19). */
      canvasSurface={frameV2 ? 'ground' : 'card'}
      /* Frame v2: the workspace tabs replace the Encounter | Chart strip, and the chart column
         replaces the band. Neither is passed there — not merely hidden by the frame. */
      topBar={
        frameV2 ? undefined : stepBar ? (
          <>
            {topBar}
            {virtualVisitNotice}
            {stepBar}
          </>
        ) : (
          topBar
        )
      }
      notice={
        stepBar && !frameV2 ? undefined : zoningOn && virtualVisitNotice != null ? (
          // `?x=intel-always-open`: a box the overlay measures, so it starts below the notice (layout only).
          <div data-slot="enc-visit-notice">{virtualVisitNotice}</div>
        ) : (
          virtualVisitNotice
        )
      }
      band={frameV2 ? undefined : patientSummary}
      bandFallback={frameV2 ? undefined : <PatientSummaryHeader patient={patient} demographics={demographics} />}
      bandExpanded={summaryExpanded}
      onBandExpandedChange={onSummaryExpandedChange}
      /* Frame v2 hands the shell NO visit strip and NO save state: both move onto the line ABOVE
         the ribbon (FrameV2StepHeader), so the ribbon gets the row to itself. */
      visitStrip={frameV2 ? undefined : visitStripNode}
      stepNav={
        frameV2 ? (
          /* Frame v2: the SAME steps, grouped S · O · A&P (or P). No Wrap-up tab: Wrap-up is the
             last block of the final section (lib/soap-ribbon.ts `sectionOf`). It switches one
             section at a time through the same handler. */
          <FrameV2StepHeader
            visitStrip={ruleCOn ? undefined : visitStripNode}
            saveState={saveState}
            // A nurse-only sign has no closing-card footer (that footer is the PROVIDER's sign), so the long page keeps main's frame v2
            // signed state for it: the "Nurse · Signed" chip and "Signed <date>" here.
            roleChip={ruleCOn && !nurseSigned ? undefined : roleChip}
            // ONE Sign note, at the end of the page; the top one is not drawn (the amend bar is elsewhere and stays).
            sign={ruleCOn ? undefined : headerSign}
            // The long page has no floating "Signed …" line above the section bar after a provider sign: the closing card's footer
            // carries who and when.
            signedLine={ruleCOn && !nurseSigned ? undefined : signedLine}
            signed={ownSignDone}
            reveal={stepBarReveal}
            onRevealed={onStepBarRevealed}
            ruleC={ruleCOn}
          >
            <SoapRibbon
              steps={navigableSteps.map((step) => ({ id: step, label: stepLabel(step, stepTitles) }))}
              activeId={currentStep}
              onStepChange={onStepChange}
              extraSections={extraSections}
              pendingOrderCount={frameV2.pendingOrderCount}
              doneSections={doneSections}
              activeSection={scrollMode ? sectionInViewId : openSectionId}
              onSelectSection={scrollMode ? jumpToSection : selectSection}
              jumpLinks={scrollMode}
              attentionSections={attentionSections}
              passedSections={passedSections}
            />
          </FrameV2StepHeader>
        ) : stepBarOwnsNav ? undefined : (
          /* The DS nav types its step ids as plain `string`; `PaStepKey` (not `EncounterStepKey`)
             because `resolveSteps` can include a PA-local key. */
          <EncounterStepNav
            steps={navSteps}
            activeId={currentStep}
            onStepChange={(id) => onStepChange(id as PaStepKey)}
          />
        )
      }
      /* D8 AUTOSAVE indicator (Samer 2026-07-22): error > saving > saved > status. */
      saveState={frameV2 ? undefined : saveState}
      progress={frameV2 ? frameV2Progress : stepBarOwnsNav ? undefined : progressPct}
      /* Pediatric GrowthChart (PROD-1324) — above the step canvas, gated by the page to peds. */
      canvasLead={
        templateNotice || growthChart || lockedNotice || (frameV2 && ambientCard) ? (
          frameV2 ? (
            /* Frame v2: the note's notices (the amend bar, a refused save, the template notices) span the
               centre column, as the note does (Ahmed 2026-10-07: min-width 100%, superseding FV2-07's 760 cap). */
            <div data-slot="frame-v2-note-notices" className="flex w-full min-w-full flex-col gap-3">
              {lockedNotice}
              {templateNotice}
              {growthChart}
              {ambientCard ? (
                <div data-slot="ambient-card-row" className="flex justify-end">
                  {ambientCard}
                </div>
              ) : null}
            </div>
          ) : (
            <>
              {templateNotice}
              {growthChart}
            </>
          )
        ) : undefined
      }
      chart={centreSwap}
      // `?x=intel-always-open` on the long page: the DS column only RESERVES the docked track (an empty spacer). The rail itself
      // is mounted once, beside the shell (`intel-host` below), and only its placement changes (docked over the track, overlay,
      // hidden), so the ambient scribe inside it never unmounts across a zoning change (Codex P1 on #931).
      note={zoningOn && ruleCOn && drawer != null ? <div data-slot="intel-spacer" aria-hidden className="h-full w-full" /> : drawer}
      {...threeColumnProps}
    >
      {frameV2 ? (
        /* Frame v2 (contract §1a, sheet v1.6): the ribbon switches S · O · A&P (or P), and the
           section shows EVERY working step it holds as a block, in one scroll. HPI · Risk · ROS
           are never tabs of their own, and Wrap-up is the LAST block of A&P / P (the design
           desk's track). The blocks come from `sectionStepsFor`, the ribbon's own grouping, so
           the canvas and the ribbon cannot disagree. Each block is the SAME step canvas the OFF
           path draws. */
        /* PROD-2743 · the approved R6c note blocks (signal-ds #434): the section is a DS NoteSection
           and each step a NoteBlock; HPI, ROS and PE draw with the structured blocks, every other
           step keeps this same StepCanvas inside its block (structured-note/structured-note-section.tsx). */
        scrollMode ? (
          /* TS-3 · ONE continuous scroll (the frame v2 default): every section of the nav, in order
             (S · O · A&P or P, Wrap-up the last block of the last), each the same StructuredNoteSection
             the one-section mode draws, all writing through the ONE live response (`sectionResponse`).
             The track jumps to a section and highlights the one in view (use-note-scroll.ts). */
          <div ref={bindNoteRoot} data-note-mode="scroll" className="flex flex-col gap-6">
            {templateRow ? (
              <p data-slot="note-template-row" className="m-0 font-sans text-size-sm text-text-secondary">
                {templateRow}
              </p>
            ) : null}
            {(() => {
              const all = sectionsFor(navigableSteps, extraSections);
              const cardOn = footOn;
              // The templates added from "/" follow the last section that holds the note's template (the added ones follow it).
              const lastTemplate = all.findLastIndex((g) => holdsTemplate(g.items));
              const node = (section: (typeof all)[number]) => (
                <Fragment key={section.id}>
                  <div {...{ [SECTION_ATTR]: section.id }} id={`soap-section-${section.id}`}>
                    {renderNoteSection({ id: section.id, title: section.title }, section.items, section.apPart)}
                  </div>
                  {lastTemplate >= 0 && all[lastTemplate] === section ? insertedPanel() : null}
                </Fragment>
              );
              // ONE status line carries the wrap-up steps and the sign (the Wrap-up progress bar is gone): see note-status-line.tsx.
              const wrapStatus = props.wrapUp?.status;
              const statusLine = (
                <NoteStatusLine
                  orderSent={wrapStatus?.orderSent}
                  showOrders={!!wrapStatus && !props.wrapUp?.signLock?.isNurse}
                  ePrescription={props.wrapUp?.ePrescription?.visible ? { synced: !!props.wrapUp.ePrescription.synced } : undefined}
                  providerSigned={providerFrozen}
                  signedBy={signedBy}
                  signedWhen={signedStamp ? formatDateTimeD45(signedStamp) : undefined}
                />
              );
              const signedFooter = providerFrozen ? (
                /* After the PROVIDER sign (rule 38; Flutter sign model): the Sign button is replaced by who and when, at the end of the
                   status line. No re-sign is offered: the page has none. */
                <div data-slot="note-end-sign" data-footer="signed" className="flex-wrap">
                  {statusLine}
                  <NoteSignActions noteActions={props.wrapUp?.noteActions} sendOrders={footSendOrders} />
                </div>
              ) : null;
              const sign =
                signedFooter ??
                (cardOn && headerSign ? (
                  /* the page's finishing action at its end: the note's own Sign, the header's handler and gates, after the pending steps. */
                  <div data-slot="note-end-sign" className="flex-wrap">
                    {statusLine}
                    {/* 22 Oct review (NoteSignActions): note actions left as labelled buttons; Send orders (N) and Sign note right. */}
                    <NoteSignActions noteActions={props.wrapUp?.noteActions} sendOrders={footSendOrders} sign={headerSign} />
                    {headerSign.reason ? (
                      <p role="alert" data-slot="note-sign-reason" className="m-0 text-status-error text-size-xs max-w-xs">
                        Not signed: {headerSign.reason}
                      </p>
                    ) : null}
                  </div>
                ) : null);
              if (!ruleCOn || all.length === 0) return all.map(node);
              /* Each main step is its own card (bridge, "note step cards"). Assessment · Plan · Wrap-up (the A section when there is one,
                 and the closing section) and the Sign footer sit inside ONE container, the closing card, dashed until the provider signs
                 (Antonin, 19 Oct: "put back the last big one with all in it, in dash"). A role with no A gets the closing section alone. */
              const from = Math.max(0, all.length - (all.length > 1 && all[all.length - 2]!.id === 'a' ? 2 : 1));
              return (
                <>
                  {all.slice(0, from).map(node)}
                  {/* `data-provider-signed`: a look-only hook (showcase-bridge.css draws the container dashed until the PROVIDER signs). */}
                  <div data-slot="note-closing-card" data-provider-signed={providerFrozen ? 'true' : 'false'}>
                    {all.slice(from).map(node)}
                    {sign}
                  </div>
                  {signBar ? (
                    <NoteSignBar
                      items={signBlankItems}
                      onGoToSign={goToFootSign}
                      // The last refusal, else the gate that disables the Sign, else the clinical gate a click would hit now
                      // (chief complaint, diagnosis): never "Ready to sign" before a refusal.
                      blockedReason={
                        headerSign?.reason ?? (headerSign?.disabled ? headerSign.title : undefined) ?? props.wrapUp?.signLock?.clinicalGateReason
                      }
                    />
                  ) : null}
                </>
              );
            })()}
          </div>
        ) : (
          renderOpenSection()
        )
      ) : (
        <>
          <StepCanvas {...props} />
          {/* Switch OFF: once, under the first template step (they are separate canvases here). */}
          {currentStep === navigableSteps.find(isQuestionnaireStep) ? insertedPanel('mt-6') : null}
        </>
      )}
      {frameV2 && (
        /* Mounted empty with the frame, so a screen reader hears each change (a region added with its
           text already in it is often not announced). Its absolutely placed `sr-only` text stays inside
           the DS step canvas, which is a positioning context since signal-ds 2.22.0 (PROD-2743). */
        <p data-slot="note-announcer" aria-live="polite" className="sr-only m-0">
          {announcement}
        </p>
      )}
    </EncounterShell>
  );
  // D-68: the compact identity line (in the workspace tab strip) draws only while the chart card is not
  // visible. OFF has no chart column and no such line, so nothing is provided.
  // The "/" templates' controller wraps the whole view (no config → the children are drawn as they were).
  const hosted = (node: ReactNode) => <InsertedTemplatesHost config={props.insertedTemplates}>{node}</InsertedTemplatesHost>;
  if (!frameV2) return hosted(shell);
  // The long page (ruleC): the page header spans the whole width above the three columns (it sat inside the centre column and was
  // cut by the side columns); the frame below takes the rest of the height (the bridge's `[data-long-page]` rules).
  return (
    <ChartCardHiddenContext.Provider value={chartCollapsed}>
      {hosted(ruleCOn ? (
        // `?x=intel-rail-catalog`: his wrap-up "Review and sign" is the jump to the note's one Sign, while there is one to give.
        <IntelRailSignContext.Provider value={catalogRail && footOn && !!headerSign && !providerFrozen ? goToFootSign : undefined}>
        <div ref={longPageRef} data-long-page="true" data-intel-close={intelCloseOn || zoningOn ? 'on' : undefined} className="flex h-full min-h-0 flex-col">
          {headerBar}
          <IntelligenceCloseContext.Provider value={closeIntel}>
            {zoningOn && drawer != null ? (
              /* `?x=intel-always-open`: the rail's ONE mount (`intel-host`), at the shell's right edge, under the page header.
                 Docked it lies over the track the DS column reserves (the spacer), 400 wide; under the 480 floor it is the
                 overlay over the centre (Antonin fc326b6d, "reopens as an overlay"; a dialog); closed it is hidden, still
                 mounted. It is never moved into or out of the DS tree, so a recording in the ambient scribe survives every
                 zoning change (Codex P1, third pass). Layout utilities only; the rail paints itself. */
              <div data-slot="intel-zone-row" className="relative flex min-h-0 flex-1 flex-col">
                {shell}
                <div
                  ref={overlayRef}
                  data-slot={intelOverlay ? 'intel-overlay' : 'intel-host'}
                  role={intelOverlay ? 'dialog' : undefined}
                  aria-label={intelOverlay ? 'Scrips Intelligence panel' : undefined}
                  hidden={!zoning?.panelShown}
                  className="absolute top-[var(--intel-host-top)] right-0 bottom-0 z-40 flex w-100 max-w-100 flex-col overflow-hidden"
                >
                  {drawer}
                </div>
              </div>
            ) : (
              shell
            )}
          </IntelligenceCloseContext.Provider>
          {intelCollapsed ? (
            /* The prototype's floating round button (IntelligenceFab), holding the DS strip's orb (its state is the ambient
               scribe's); it reopens the rail. The DS's collapsed column is not drawn (the bridge). */
            <IntelligenceFab ref={intelFabRef} onOpen={openIntel}>
              {frameV2.intelligenceStrip}
            </IntelligenceFab>
          ) : null}
        </div>
        </IntelRailSignContext.Provider>
      ) : (
        shell
      ))}
    </ChartCardHiddenContext.Provider>
  );
}

/** PROD-2962 (D-67): how long the Docs block says "Loading…" before it says the documents are not there. */
export const DOCS_WAIT_MS = 15_000;

/**
 * The frame v2 Docs block before its list arrives. The page hands the list once the visit's encounter id
 * is known, and it only opens the frame after that id exists, so "Loading…" is normally a flash. If the
 * list has still not arrived after DOCS_WAIT_MS, the block says so honestly and what to do — never an
 * endless "Loading…" (D-67). A failure to READ the documents is the list's own (DocumentsChartTab).
 */
function DocsPending() {
  const [waitedOut, setWaitedOut] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setWaitedOut(true), DOCS_WAIT_MS);
    return () => clearTimeout(timer);
  }, []);
  if (waitedOut) {
    return (
      <p data-slot="docs-unavailable" role="status" className="m-0 font-sans text-size-sm text-text-secondary">
        This visit's documents are not available yet. Reload the page to try again.
      </p>
    );
  }
  return (
    <p data-slot="docs-loading" className="m-0 font-sans text-size-sm text-text-secondary">
      Loading this visit's documents…
    </p>
  );
}

/** One working step's canvas: the section, the step chrome and its body. The OFF path renders
 *  exactly one (the active step); frame v2 renders one per step of the active S·O·A·P section.
 *  Everything here was the body of `EncounterShellView` until 1 Oct (moved verbatim). */
interface StepCanvasProps extends EncounterShellViewProps {
  /**
   * Frame v2 only: ONE live response shared by every block of the open section. Several renderers
   * (HPI · Risk · ROS) merge into the same whole-encounter response; each merging into its own
   * commit-synced copy would let two emits in one tick replace each other's answers (Codex P1,
   * 1 Oct). Absent (OFF): the block keeps its own copy, exactly as before.
   */
  sharedResponse?: MutableRefObject<QuestionnaireResponse>;
  /** Frame v2 only (TS-3): which part of the A/P step this canvas draws — the assessment (A) or the plan
   *  (P). Absent: the whole A/P, as before. */
  apPart?: ApPart;
  /**
   * Frame v2 only: draw the step WITHOUT its section header (EncounterSectionChromeHost), because a
   * DS NoteBlock around it already names the step. The section's free-text narrative, which that
   * header used to open, is drawn by the NoteBlock's host instead (structured-note-section.tsx).
   * Absent (OFF): the header renders exactly as before.
   */
  bare?: boolean;
  /**
   * Frame v2 only: the canvas is frozen by a provider sign (PROD-2810 / rule 38), so the renderer draws
   * in review mode whatever the step. Absent (OFF): the mode rule below is exactly as before.
   */
  frozen?: boolean;
  /**
   * Frame v2 only: the canvas is drawn inside a note block. A&P and Wrap-up then draw no title of their
   * own and no "coming soon" control, and Wrap-up has no Sign quill (the note header signs) — the
   * 6 Oct audit, FV2-04 / FV2-05 / FV2-06. Absent (OFF): exactly as before.
   */
  inNote?: boolean;
}

/**
 * [PROD-3045] Frame v2: the findings the nurse's "Today's plan" block proposes as nursing problems — only
 * what this note already holds (the template's Risk lines, its scored totals, and the vitals block's
 * backend flags), each "from" the block it sits in. Pure: ./lib/nursing-problems.ts.
 */
function noteNursingFindings(
  questionnaire: Questionnaire | undefined,
  response: QuestionnaireResponse,
  resolveVitals: QuestionnaireRendererProps['resolveVitals'],
  stepTitles: StepTitleOverrides | undefined,
  managedRisk?: RiskManaged,
): NursingProblem[] {
  // D-74 (lane A): with no `vitals-block` marker in Objective the frame draws the Vitals block first in Objective, from
  // the same resolver — so its flags are today's findings too (PROD-2947, integrating lanes A and G).
  const objective = !objectiveHasVitalsMarker(questionnaire, ['pe']);
  const marker = questionnaire ? (objective ? OBJECTIVE_VITALS_MARKER : vitalsMarkers(questionnaire.item)[0]) : undefined;
  return nursingFindings({
    questionnaire,
    values: answerMap(response),
    vitals: marker ? resolveVitals?.(marker)?.items : undefined,
    objectiveVitals: objective,
    sourceOf: (step) => stepLabel(step, stepTitles),
    ...(managedRisk ? { managedRisk } : {}),
  });
}

function StepCanvas(allProps: StepCanvasProps) {
  const { sharedResponse, bare, frozen, apPart, inNote, ...props } = allProps;
  const {
  role,
  currentStep,
  questionnaire,
  stepTitles,
  templateName,
  response,
  onResponseChange,
  riskReady = true,
  riskReadFailed,
  onRiskDone,
  signed,
  assessmentPlan,
  alliedPlan,
  nursingPlan,
  wrapUp,
  resolveVitals,
  resolveChiefComplaint,
  chiefComplaints,
  docs,
  templateUnavailable,
  onAddOrder,
  onAddToFavourites,
  } = props;
  // HPI/ROS/PE/Risk are SECTIONS of one shared Questionnaire (PROD-1234): the active step selects
  // which section renders. When the active section has no authored groups, the step degrades to the
  // template-gate-relax notice (A/P + Wrap-Up are template-independent and stay mounted regardless).
  const onQuestionnaireStep = isQuestionnaireStep(currentStep);
  const railStep: RailStep | undefined = currentStep === 'ros' || currentStep === 'pe' ? currentStep : undefined;
  // PROD-2789 — the complaint's suggested system order. `systemOrderFor` answers `undefined` on
  // every step but ROS / PE, and for no complaint or an unmapped one, so the renderer then gets no
  // prop at all and keeps the authored order. The same row always returns the same array, so an
  // unchanged complaint hands the renderer an identical prop.
  const systemOrder = systemOrderFor(currentStep, chiefComplaints);
  // TODO(scrips-signal-ds #423): pass `systemOrder` directly once the DS release types it. `satisfies`, not `as`: if the release types it differently, this stops compiling.
  const systemOrderProps = (systemOrder ? { systemOrder } : {}) satisfies Partial<QuestionnaireRendererProps> & { systemOrder?: readonly string[] };

  // ── THE TEMPLATE DERIVATION CHAIN — ONE memo, keyed on the TEMPLATE only ────────────────────
  // Everything here is a pure function of (questionnaire, currentStep): section slice, narrative
  // item, chief-complaint marker, the two strips and the symptom-chip
  // defaults. None of it reads `response`.
  //
  // It used to be six plain render-body consts, so every keystroke re-walked and REBUILT the
  // section tree and handed `QuestionnaireRenderer` a questionnaire with a brand-new object
  // identity — which is exactly what the DS renderer memoises its `computed`/`serialise`/`set`
  // work on, so its internal caches were invalidated on every character typed. Memoising here is
  // what gives the renderer a STABLE template across a burst of typing.
  //
  // `response` is deliberately NOT a dependency: the response flows to the renderer through its
  // own `response` prop and through the merge handlers below, both of which still see every
  // keystroke. What is stabilised is the TEMPLATE, never the answers.
  const {
    sectionQuestionnaire,
    sectionHasContent,
    narrativeItem,
    ccHpiMarker,
    renderQuestionnaire,
    rosIsFlatShape,
  } = useMemo(() => {
    const section =
      onQuestionnaireStep && questionnaire ? questionnaireForStep(questionnaire, currentStep) : undefined;
    const hasContent = onQuestionnaireStep && stepHasContent(questionnaire, currentStep);
    // ADDED NOTES (PROD-1368): the section's authored free-text narrative item, if any. The renderer
    // gets the section with that item STRIPPED (else it double-renders as a one-line field); the
    // ADDED NOTES editor owns it, reading/writing the SAME single QuestionnaireResponse by linkId.
    // Stripping it from the renderer's merge scope too keeps the narrative answer untouched by
    // structured-field edits (it falls outside that scope and is preserved by mergeSectionResponse).
    const narrative = section ? findNarrativeItem(section.item) : undefined;
    // PROD-2136 — the Studio's chief-complaint HPI block marker (HPI only). The DS renderer draws
    // the block at this group's own position (`resolveChiefComplaint`); this host only needs to
    // KNOW a marker exists, to keep it out of the chip rail's jump targets (below). PROD-2577
    // part B retired the PA-drawn editor/cards that used to need this too — `marker` now exists
    // for the rail filter alone. No marker → `undefined` → every line below behaves exactly as
    // before.
    const marker =
      currentStep === 'hpi' && section ? findChiefComplaintHpiMarker(section.item) : undefined;
    let stripped = section;
    // stripNarrative, not stripItem: a notes-only group (converter templates, PROD-2791) goes with its
    // narrative instead of staying behind as an empty heading.
    if (stripped && narrative) stripped = stripNarrative(stripped, narrative.linkId);
    // The Studio's sample category cards beside the marker are canvas-only (Ahmed, 2026-09-28):
    // the PA's categories come per complaint from the server, inside the block.
    if (stripped && marker) stripped = stripHpiSampleCategories(stripped);
    // THE MARKER IS NO LONGER STRIPPED (Q2, 2026-09-16). It used to be, because the block was a
    // PA-local component this host mounted above the renderer — so the group had to be hidden
    // from the renderer to stop it drawing an empty section as well. Now the block is a Signal DS
    // component and the DS renderer draws it AT the marker, through `resolveChiefComplaint`.
    // Leaving the group in is what lets the author's chosen position actually take effect.
    // PROD-2577: the Risk step's Fall/Pain groups are NO LONGER stripped. The DS renderer draws
    // them (the controls are named `fall-assessment` / `wong-baker-pain`; since the 7 Oct 2026 ruling
    // the current tools are CDC STEADI and NRS 0–10, Morse / Wong-Baker are read-only history); the page bridges their answers to
    // EncounterMetaData, so there is still one owner per datum — it is just no longer a bespoke panel.
    // PROD-2577 part B: the `vitals-block` marker is NO LONGER stripped. The group now resolves
    // through `resolveVitals` (like the chief-complaint marker resolves through
    // `resolveChiefComplaint`), so the DS renderer draws the block AT the marker's own position —
    // stripping it would put the section rail back to descending no further than "Physical Exam"
    // for the same reason described on the chief-complaint marker above.
    // THE STANDARD (DS-033): ROS boolean findings render as tri-state SymptomChips — the section
    // adapter defaults `itemControl='symptom-chip'` where the wire template authors none (see
    // withSymptomChipDefaults — the seam). Rendering hint only: linkIds and the response-merge
    // scope are unchanged, so `strippedQuestionnaire`/`sectionQuestionnaire` stay valid for merges.
    const render =
      onQuestionnaireStep && stripped ? withSymptomChipDefaults(stripped, currentStep) : stripped;
    // WHICH ROS LAYOUT (once §26 of the PA's encounter-design-overrides.css, deleted PA-Z 2026-09-23;
    // now the DS `EncounterStepPanel rosShape`). The card-grid layout is keyed on
    // the DOCUMENT SHAPE: with one top-level group per body system each system IS its own qr-block,
    // so §25's region grid matches nothing and the blocks stack full-width with no card chrome
    // (measured in the Phase 4 parity capture, 2026-09-15). The Studio-authored template emits
    // exactly that flat shape — wire contract rule 1, "ROS flattens on the write side". Shape, not
    // step: dev still serves the WRAPPED template until this one is published, and that shape must
    // keep §25. `data-ros-shape="flat"` is named for the shape it detects, nothing else.
    const rosFlat = currentStep === 'ros' && section != null && isFlatGroupShape(section);
    return {
      sectionQuestionnaire: section,
      sectionHasContent: hasContent,
      narrativeItem: narrative,
      ccHpiMarker: marker,
      renderQuestionnaire: render,
      rosIsFlatShape: rosFlat,
    };
  }, [questionnaire, currentStep, onQuestionnaireStep]);

  /** Scroll a rail chip's group into view — the host chrome's rail, which only RISK still uses.
   *  ROS and PE draw their rail inside the DS renderer (`sectionRail`, DS 2.2.0), which owns its
   *  own jump + 2 s highlight; Risk moves the same way in PROD-2577 part B, and this goes with it. */
  const jumpToGroup = (linkId: string) => {
    if (!sectionQuestionnaire) return;

                  // Deep lookup: the rail's targets can be nested one level below the top
                  // (see railGroups), and a top-level-only find returns undefined for those —
                  // which silently made every PE chip a no-op.
                  const t = findGroupDeep(sectionQuestionnaire, linkId)?.text;
                  if (t && typeof document !== 'undefined') {
                    // FLAT shape (ROS): each group is its own block, labelled with its title.
                    const block = document.querySelector(
                      `[data-testid="qr-block"][aria-label="${CSS.escape(t)}"]`,
                    );
                    if (block) {
                      block.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    } else {
                      // NESTED shape (PE): the DS renders ONE block for the wrapper and each
                      // category as a nested <section aria-label={group.text}> inside it
                      // (`QuestionnaireRenderer.tsx`, the renderItem group branch — 1.64.0 and
                      // 1.65.0 alike). That aria-label IS the anchor.
                      //
                      // This used to walk divs for one whose textContent equalled the title. That
                      // was wrong on its own terms — the anchor existed all along — and DS 1.65.0
                      // made it fail outright: PROD-2195 puts "All Neg" and "Clear" inside every
                      // nested category header, so the header's textContent became
                      // "GeneralAll NegClear" and the predicate matched nothing. Every PE chip
                      // went back to being a silent no-op. Matching the aria-label is immune to
                      // whatever the DS later renders INSIDE the header.
                      //
                      // Scoped under a qr-block so it cannot match the rail chip bearing the same
                      // words, and `CSS.escape`d because category titles carry parentheses —
                      // "Genitourinary (female)" is a real one.
                      const nested = document.querySelector(
                        `[data-testid="qr-block"] section[aria-label="${CSS.escape(t)}"]`,
                      );
                      if (nested) {
                        nested.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }
                  }
  };

  // SECTION-level "All Neg"(ROS) / "All Normal"(PE) — PROD-1588 interaction fix I2 (Flutter
  // `makeAllItemNegative`): the DS chrome's rail end-slot seam, offered only when the active
  // section actually renders ≥1 enabled tri-state chip (anti-fiction) and the encounter isn't
  // signed (a finalized note must not be bulk-mutated; autosave is equally `!signed`-gated).
  // The flip rides the SAME dispatch a single chip uses — an emitted response through
  // `mergeSectionResponse` → ONE `onResponseChange` call → one autosave debounce.
  // Memoised on the response too — it reads answers, so it MUST re-run per keystroke; the memo
  // only spares the walk on the renders that change neither the template nor the answers.
  const allNegChipIds = useMemo(
    () => (!signed && renderQuestionnaire ? sectionSymptomChipIds(renderQuestionnaire, response) : []),
    [signed, renderQuestionnaire, response],
  );

  // The LIVE response, readable from an event handler without being a dependency of it. The
  // renderer's `onChange` merges its section emit into the whole-encounter response; closing over
  // `response` would give that callback a new identity on every keystroke and push a new `onChange`
  // into the DS renderer each time. Handlers run in the event phase, long after this assignment,
  // so they always read the response from the most recent render — the same value the closure
  // would have carried.
  // SYNCED IN AN EFFECT, not assigned during render: `react-hooks/refs` ("Cannot update ref during
  // render") fails the lint on the assign-in-render shorthand, and the rule is right — a ref
  // written during render is invisible to React's own scheduling. The effect runs after every
  // commit and before any user event can reach a handler, so a handler always reads the response
  // from the most recent render.
  const responseRef = useRef(response);
  useEffect(() => {
    responseRef.current = response;
  }, [response]);
  // ONE stable `onChange` for the DS renderer. Answers still flow on EVERY keystroke: the emit `r`
  // is merged into the latest response by linkId (`mergeSectionResponse`) and dispatched through
  // `onResponseChange` exactly as before — only the function's identity is now stable.
  const handleRendererChange = useCallback(
    (r: QuestionnaireResponse) => {
      // The merge SCOPE. Unreachable-when-undefined in practice — the renderer only mounts inside
      // the `sectionQuestionnaire` arm — but the callback is declared above that narrowing, so the
      // guard is what keeps the scope non-optional instead of widening `mergeSectionResponse`.
      const scope = renderQuestionnaire ?? sectionQuestionnaire;
      if (!scope) return;
      if (sharedResponse) {
        // Merge into the section's ONE live response and advance it NOW, so a second block's emit
        // in the same tick builds on this one instead of on the last committed render.
        const next = mergeSectionResponse(sharedResponse.current, scope, r);
        sharedResponse.current = next;
        onResponseChange(next);
        return;
      }
      onResponseChange(mergeSectionResponse(responseRef.current, scope, r));
    },
    [onResponseChange, renderQuestionnaire, sectionQuestionnaire, sharedResponse],
  );

  /* Section notes disclosure. The design shows no notes box on HPI/ROS/PE — the Comments glyph
     in the section header is the affordance, and the note appears when pressed (design 343 ·
     428 · 484). An EMPTY note starts closed so the canvas matches the design at rest; a section
     whose note HAS text starts open. Closed-with-text read as a lost note after every reload
     (dev repro 2026-10-10: the narrative was saved and returned by the API, but hidden behind an
     icon that said "Add section note"). The clinician's own toggle wins, per step, for as long as
     this view is mounted. Local UI state: opening a note changes nothing that persists. */
  const [notesToggled, setNotesToggled] = useState<Partial<Record<PaStepKey, boolean>>>({});
  const noteHasText = narrativeItem ? answerString(response, narrativeItem.linkId).trim() !== '' : false;
  const notesOpen = notesToggled[currentStep] ?? noteHasText;
  return (
    <>

      {/* CORRECTED 2026-09-16, UPDATED PROD-2577 part B (2026-09-24). The HPI chief-complaint
          surface is no longer drawn by this host at all — it is entirely inside the DS
          `ChiefComplaintHpiBlock`, at the Studio-authored marker's own position, through
          `resolveChiefComplaint`. So it lives INSIDE the
          `onQuestionnaireStep && sectionHasContent && sectionQuestionnaire` arm that opens
          below (a template with no authored HPI section falls to the `step-not-documented` arm
          and renders no chief-complaint surface at all) and it is template-DEPENDENT: it draws
          only where the section authors the `chief-complaint-hpi` marker.

          What this canvas actually holds, in order: the authored
          section arm (section chrome · PE category rail · the QuestionnaireRenderer, which
          draws PE vitals and Chief Complaint wherever their Studio markers sit) · the
          "Not documented" arm · the non-questionnaire steps. */}
      {onQuestionnaireStep && sectionHasContent && sectionQuestionnaire ? (
        // The active step's SECTION of the shared Questionnaire. The renderer only knows this
        // section, so its emit is merged back into the whole-encounter response by linkId —
        // editing one section never wipes another (one QuestionnaireResponse, PROD-1234).
        // The questionnaire step panel — the DS `EncounterStepPanel` (graduated from this file,
        // 2026-09-23). It emits `data-slot="qr-step-panel"` (the stable root the DOM-regression
        // capture measures), `data-step`, and `data-ros-shape="flat"` — the PROD-2351 shape
        // DETECTOR for the Studio-authored flat ROS document. The DS drops the column gap on PE
        // (Ahmed, 2026-09-14: it stacked on the vitals block's own spacing).
        /* ONE header → body gap on all four steps: --spacing-6 (24px). The DS panel drops its
           `gap-6` on `pe` (Ahmed 2026-09-14) because it stacked on `EncounterStepVitals`' own
           spacing — but this host no longer mounts that part (vitals render inside the renderer at
           the `vitals-block` marker, with no top margin; see `encounter-shell-page.tsx`), so PE's
           header sat flush on the vitals row at 0px while HPI / ROS / Risk sit 24px below theirs.
           24 is also production's number: `verticalSpace24` above `vitalsBar`
           (`physical_exam_screen.dart:77-88`). The DS `cn` merges it; a no-op on the other steps. */
        <EncounterStepPanel
          step={currentStep}
          rosShape={rosIsFlatShape ? 'flat' : undefined}
          className="gap-6"
        >
          {/* HPI/section chrome (Slice 3 B2–B4): header action row + group chip-rail + ADDED
              NOTES, above the §6a block-grid. Anti-fiction affordances ship disabled.
              gap-6 (24px): the QR frame's added-note → first question group run (~25px,
              qr-style vertical rhythm) — was gap-4 (16), part of the round-4 density pass. */}
          {!bare && (
          <EncounterSectionChromeHost
            /* C4 / PROD-1588: every questionnaire step (hpi · risk · ros · pe) mounts the
               DS-graduated chrome — DS 1.52 shipped the `risk` EncounterSectionKind (bolt
               glyph, cure wave 5), so the wave-4 kind-less fallback is retired. */
            kind={currentStep}
            sectionTitle={DESIGN_SECTION_TITLE[currentStep]}
            /* The questionnaire steps' title tracking (-0.01em) and rail hairline (wave-3 1B; were
               overrides :431 / :334 / :1940). ONE hairline on all four steps (tabs-polish,
               2026-09-26): --color-border-default, the same line the DS section rail draws under
               ROS / PE (`SectionCategoryRail`). ROS used to keep the DS default strong line, but
               ROS no longer draws a chrome rail (its row moved into the renderer, PROD-2577), so
               only Risk renders this hairline today — the per-step split described a row that is
               gone. No visual change today; it keeps a future chrome rail on the shared line. */
            titleTracking="tight"
            railBorder="soft"
            /* Design: the notes surface is opened from the header Comments icon,
               not shown at rest (§11). */
            showNotes={notesOpen}
            onToggleNotes={() => setNotesToggled((t) => ({ ...t, [currentStep]: !notesOpen }))}
            /* The two SHARED cluster icons. Wired only when the host supplies a handler, so
               Wrap-up (which has neither) renders them disabled exactly as before. */
            onAddOrder={onAddOrder}
            onAddToFavourites={onAddToFavourites}
            /* D7: the active template's name → the chrome's read-only pill. */
            templateLabel={templateName}
            /* THE CHROME RAIL IS RISK-ONLY NOW (PROD-2577). ROS and PE draw their header row —
               system chips, per-system All Neg circles, the tab's All Neg and "Clear ROS" /
               "Clear PE" — inside the DS renderer (`sectionRail`, below), derived from the
               template's own groups, so the Studio preview draws the same row. Passing no groups
               here also drops the chrome's own Clear (it renders only beside a rail), so neither
               step shows two. HPI never had one (the design goes header -> field -> cards).
               Risk keeps this rail until PROD-2577 part B moves it into the template too.
               `railGroups` descends through a lone wrapper whose children are all groups. */
            groups={currentStep !== 'risk' ? [] : railGroups(sectionQuestionnaire)
              // The block's marker group is not a jump target — the block itself is.
              .filter((g) => !ccHpiMarker || g.linkId !== ccHpiMarker.linkId)
              // Nor is a group that holds only the narrative: the ADDED NOTES editor owns it.
              .filter((g) => !narrativeItem || !holdsOnlyNarrative(g, narrativeItem.linkId))
              .map((g) => ({
              linkId: g.linkId,
              // The rail chip shows the group's authored abbreviation — carried on
              // `code[]` by the wire contract (PROD-2351) — and falls back to the group's
              // full text when none is coded.
              title:
                railAbbreviation(g) ??
                g.text ??
                g.linkId,
              // Per-group completion dot (DS wave-2 R3/B6 seam): filled once any answer
              // lands inside the group's linkId scope — driven off the live response.
              complete: groupHasAnswer(response, g),
            }))}
            // Section-level bulk-negative for Risk (the only step still on the chrome rail).
            // Only when negatable chips exist and the encounter isn't signed (`allNegChipIds` is
            // `!signed`-gated where it is built). One wording, "All Neg", for every surface.
            // G7: the DS bulk guard — fills UNANSWERED findings only and never writes a
            // bulk-exempt (suicide / self-harm / `cps-bulk|exempt`) item; it used to overwrite.
            allNegLabel="All Neg"
            onAllNeg={
              currentStep === 'risk' && allNegChipIds.length > 0 && renderQuestionnaire
                ? () => onResponseChange(fillSectionNegative(response, renderQuestionnaire))
                : undefined
            }
            onClearSection={() =>
              // Clear the section via the SAME merge-by-linkId path individual edits use:
              // feeding an empty section response drops every section answer and keeps all
              // off-section answers — one QR contract, not a second clear-shape. (#128 bot)
              // Scope = the FULL section (incl. the narrative), so Clear AC clears notes too.
              onResponseChange(mergeSectionResponse(response, sectionQuestionnaire, { answers: [] }))
            }
            /* Per-category all-negative from the Risk rail — Flutter gives every category pill
               its own mark-negative glyph (common_encounter_component_base_tab.dart:658-697).
               Same merge-by-linkId dispatch as the section-wide action, scoped to the group. */
            onAllNegGroup={
              currentStep === 'risk' && allNegChipIds.length > 0 && renderQuestionnaire
                ? (linkId) =>
                    onResponseChange(fillGroupNegative(response, renderQuestionnaire, linkId))
                : undefined
            }
            onJumpToGroup={currentStep === 'risk' ? jumpToGroup : undefined}
            notes={
              narrativeItem
                ? {
                    linkId: narrativeItem.linkId,
                    label: narrativeItem.text,
                    value: answerString(response, narrativeItem.linkId),
                    onChange: (v) => onResponseChange(setAnswer(response, narrativeItem.linkId, v)),
                  }
                : undefined
            }
          />
          )}
          {/* PE VITALS — PROD-2577 part B: rendered by the DS renderer at the Studio-authored
              `vitals-block` marker's own position (via `resolveVitals`, passed to
              QuestionnaireRenderer below), the same way the chief-complaint block renders at
              its own marker. This host no longer hardcodes it above the section — the template
              decides where it sits, same as Flutter's fixed header-then-vitals-bar order
              (`common_encounter_component_base_tab.dart:161-192`,
              `physical_exam_screen.dart:77-89`) reproduces only while the Studio keeps
              authoring the marker there. */}
          {/* PE's category shortcut row is no longer composed here (PROD-2577): the DS renderer
              draws it (`sectionRail`) as the first thing inside the form, which keeps Flutter's
              order — header (`common_encounter_component_base_tab.dart:161-192`), the vitals
              bar above (`physical_exam_screen.dart:77-89`), then the pinned category row
              (`:94-108`, `:510-609`), then the blocks. */}
          {/* The design (lines 374-413) puts the attribute-card grid inside a grey-100
              COLLAPSIBLE section behind an uppercase brand heading with a rotating caret.
              The design shows one such section PER COMPLAINT ("HPI Eye Pain" / "Ear Pain" /
              "Cough"); this app renders ONE questionnaire section for the whole step, so
              there is one section here, titled by the step. Per-complaint scoping needs the
              template authored that way — it is not something the host can synthesise
              without duplicating the same questions under every complaint. */}
          <EncounterStepSection step={currentStep}>
            {/* The step-level HPI disclosure was REMOVED on Ahmed's instruction
                (2026-09-12: "remove the HPI colabsable level, just keep the complain
                container which is collabsable"). Collapsing now lives on ONE level only —
                each complaint's own wrapper inside ChiefComplaintHpiBlock — which is what
                the deployed reference does (chief_complain_screen.dart:152-204, the
                InkWell on the complaint heading). ROS and PE never had this wrapper.
                The DS `EncounterStepSection` emits `<section><div id="qr-section-{step}">`
                verbatim (the PA tests and measure.mjs target the id). */}
          {/* PROD-2136 — the chief-complaint HPI block, where the template authors the
              marker: one card per complaint (heading "{display} ({category})"), its
              server-resolved attribute groups as toggle chips, answers saved to the
              complaint's `patientHpi` through the SAME CreateChiefComplaint replace-all the
              editor above uses. Any groups authored BESIDE the marker still render through
              the DS renderer below it; when the marker is the only HPI content the block
              replaces the flat section outright. */}
          {/* THE BLOCK — AND, since PROD-2577 part B, its picker and remove affordance too — NO
              LONGER MOUNTS HERE. It is a Signal DS component, and the DS renderer draws it at
              the marker's own position inside the questionnaire, through `resolveChiefComplaint`
              below, which now also supplies `onAdd`/`onSearch`/`onRemove`. So the marker is not
              stripped and this host does not decide where the block — or its create/remove
              affordances — go. */}
          {/* PROD-2577 (closes PROD-2354): Risk renders its authored Fall/Pain groups HERE, like
              every other step — the DS draws the `fall-assessment` / `wong-baker-pain` controls (7 Oct 2026
              ruling: CDC STEADI and NRS 0–10 are the current tools; Morse / Wong-Baker are read-only history) — and the page
              bridges those answers to EncounterMetaData. Held back until `riskReady`, so the Risk
              controls mount with the stored answers already in `response` (a Base UI control
              fixes controlled-vs-uncontrolled at mount and ignores an answer that arrives later). */}
          {currentStep === 'risk' && riskReadFailed && (riskReadFailed.fall || riskReadFailed.pain) && (
            <Callout
              severity="warning"
              title={`The saved ${riskReadFailed.fall && riskReadFailed.pain ? 'fall and pain assessments' : riskReadFailed.fall ? 'fall assessment' : 'pain assessment'} could not be loaded`}
            >
              {`Changes to ${riskReadFailed.fall && riskReadFailed.pain ? 'them' : 'it'} will not be saved, so the stored record is not overwritten. Reload the encounter to try again.`}
            </Callout>
          )}
          {(currentStep !== 'risk' || riskReady) && (
          <QuestionnaireRenderer
            questionnaire={renderQuestionnaire ?? sectionQuestionnaire}
            /* The DS renders this as `<form role="form">` and names it
               `aria-label ?? questionnaire.title ?? "clinical template"`. None of the four
               questionnaire-sourced steps carries a `title` — the step's name lives on the
               PA side, not on the resource — so HPI, ROS, PE and Risk ALL announced the same
               string, "clinical template", and a screen-reader user moving between steps got
               no signal that the form had changed. `stepLabel` is the same source the visible
               tab and the empty-state use (:1301, :1320), so the spoken name and the seen one
               cannot drift, and an author's `cps-layout` rename is carried into the
               accessible name for free rather than needing its own plumbing. */
            aria-label={stepLabel(currentStep, stepTitles)}
            response={response}
            onChange={handleRendererChange}
            /* I4 per-group comment bubbles — DATA-GATED, `onComment` deliberately NOT passed
               (PROD-1588 wave 5 verdict, 2026-07-22; PROD-1361 follow-up). The DS composer
               is ready (bubble renders the moment this prop lands), but NO wire can hold the
               comment on this save path:
                 · Flutter persists per-group comments OUTSIDE the QuestionnaireResponse — a
                   plain `comment`/`notePlainText` on EncounterTemplateCategoryModel POSTed to
                   the legacy CreateROS/CreatePE/CreateChiefComplaint endpoints
                   (encounter_data_source_impl.dart) — the very dual-write this page RETIRED
                   for the FHIR-native QB persist (see handleSave).
                 · The QB wire (QuestionnaireResponseResource.cs, origin/master) has NO
                   note/comment/extension field on items — a comment sent would be silently
                   dropped server-side. Inventing a synthetic-linkId convention is forbidden
                   (never guess the field).
                 · DS 1.53 QuestionnaireRenderer also has no seam to DISPLAY a persisted
                   comment (the composer holds only the open draft), so even a persisted
                   comment could not render on reload.
               Unblock = QB grows an item-level note/extension + DS grows a comments-display
               prop; then wire onComment through the same merge/autosave dispatch. */
            /* ONE wording, "All Neg", on every bulk-negative control — the per-block action, the
               rail circle's label and the tab-level action alike (the EncounterScreen design,
               2026-08-06, reversed PE's earlier "All Normal"). Passed explicitly rather than left
               to the DS default so the copy is owned in one visible place. */
            allNegLabel="All Neg"
            /* THE SECTION RAIL (PROD-2577, DS 2.2.0) — ROS and PE only. The header row is DERIVED
               from the template's groups by the renderer: a chip per system (`cps-abbreviation`),
               jump + 2 s highlight, a circle per system that has a posneg finding (All Neg for that
               system; success tone when every posneg is negative), the tab's All Neg, and the tab
               Clear behind a confirm. Every action writes through `onChange` — the same
               `handleRendererChange` merge a chip tap uses — so it lands in the one
               whole-encounter QuestionnaireResponse and autosaves like any edit. NOT pinned —
               it scrolls away with the section (Ahmed 2026-09-26: "remove that static part"). The confirm copy is production's verbatim (`onClearTap`,
               `common_encounter_component_base_tab.dart:622-630`), including its missing "to". */
            sectionRail={railStep ? { pinned: false } : undefined}
            /* THE STUDIO PAGE WRAPPER DRAWS NO CHROME (PROD-2577). Template Studio publishes each
               page as one top-level group (`hpi`, `risk-assessment`, `ros`, `pe`), so the step's
               section is that single wrapper — which the renderer drew as a titled section with
               its own header and Clear ("HPI / HPI / Clear" above the chief complaint). The step
               chrome above already names the step, as production does. The DS skips the Risk card
               wrapper and any wrapper the section rail already descends through, so this changes
               HPI only; ROS / PE lose theirs through the rail. Render-only: linkIds and the
               response shape are unchanged. */
            unwrapPageGroup
            clearLabel={railStep ? RAIL_CLEAR_LABEL[railStep] : undefined}
            confirmClear={railStep ? { body: RAIL_CLEAR_CONFIRM_BODY } : undefined}
            /* SIGNED ROS / PE ARE READ-ONLY (PROD-2577). The section rail's All Neg, circles and
               Clear, the per-block All Neg / Clear and every chip follow this mode, so a signed
               note renders them inert instead of offering a bulk mutation (the old host rail's
               `!signed` gate, now expressed where the actions live). The jump chips stay usable.
               HPI is not included on this switch-off canvas: it stays editable after sign, its edits
               held for "Save amendment" (PROD-2607). With frame v2, `frozen` (a provider sign,
               PROD-2810 / rule 38 — the register row amended 2026-10-06) draws review mode. */
            mode={frozen || (signed && railStep) ? 'review' : 'capture'}
            /* The canvas-entry fade and the pointer cursor on every enabled control inside the
               form — the renderer owns both now (DS-E2; were overrides :1103-1114 and the
               step-canvas cursor rule). */
            enterMotion
            buttonCursor="pointer"
            /* ROS and PE lay their systems out on PRODUCTION'S responsive rule, `'auto'` =
               floor(width / 400) columns, min 1 (Flutter `max(1, constraint.maxWidth ~/ 400)`,
               review_of_system_screen.dart:86 / physical_exam_screen.dart:332; dev-provider
               measured 2026-09-25: 4 across at ~1770px). It was a fixed 3, which the ROS anchor
               (2 across at its frame width) and production both contradict. The wide 24/32
               gutter stays; ROS's top-level block grid takes the same wide gutter; PE's block
               title is visually hidden because the chrome above already carries the same words
               (wave-3 1B; were overrides :1560 / :1600 / :1783). HPI and Risk take the renderer
               defaults for the region props.
               ONE BLOCK GUTTER FOR ALL FOUR STEPS (tabs-polish, 2026-09-26): every top-level block
               is the same shared encounter card on every step, so they sit on the same 24/32
               (--spacing-6 / --spacing-8) gutter — HPI's authored groups used to pack at the 12px
               default while ROS's cards sat at 24. A single-block grid (Risk's card wrapper, PE's
               rail body) has no gutter to show, so this changes HPI's multi-block grids only. */
            blockGap="wide"
            regionColumns={currentStep === 'ros' || currentStep === 'pe' ? 'auto' : undefined}
            regionGap={currentStep === 'ros' || currentStep === 'pe' ? 'wide' : undefined}
            blockTitle={currentStep === 'pe' ? 'visually-hidden' : undefined}
            /* The DS `role` prop takes the DS's own ClinicalRole union, which has NO
               'allied-health' member (PROD-2040 note in src/auth/use-role.ts). This is the
               boundary narrowing: allied-health keeps falling through to 'physician' here,
               exactly as it did before this change. Widening it needs the DS union to gain
               the member first — not a local fork of the union. Only reached on the four
               questionnaire steps anyway; the plan step does not mount this renderer. */
            role={role === 'nurse' ? 'nurse' : 'physician'}
            /* PROD-2136 / Q2 — the chief-complaint HPI block. The authored marker group
               says WHERE; this says WHAT, because the complaints and their attribute
               history live in the complaint document, not in the Questionnaire. Passed
               unconditionally: the DS only calls it for a group carrying the
               `chief-complaint-hpi` itemControl, so a template without one never reaches it,
               and a template WITH one is never silently blank. */
            resolveChiefComplaint={resolveChiefComplaint}
            /* PROD-2577 part B — the PE vitals block. Same shape: the authored `vitals-block`
               marker says WHERE, this says WHAT (the readings are observations, not answers).
               Passed unconditionally — the DS only calls it for that one itemControl. */
            resolveVitals={resolveVitals}
            /* PROD-2577 — the Fall/Pain risk cards' "Done" button (DS #401, capture mode only).
               `useRiskBridge.onRiskDone` owns the effect (marks the head entry completed through
               the same local-then-batched-save path every Risk edit uses). */
            onRiskDone={onRiskDone}
            /* PROD-2789 — ROS / PE only: the chief complaint's systems open first. The DS moves
               plain groups by linkId prefix and never touches an answer; without the prop the
               render is unchanged. */
            {...systemOrderProps}
          />
          )}
          </EncounterStepSection>
        </EncounterStepPanel>
      ) : onQuestionnaireStep && templateUnavailable ? (
        /* G11: the template could not be read — never the "Not documented" stub, which reads as an
           empty section. Retry sits on the banner above the canvas, on every step. */
        <Callout severity="warning" title={`${stepLabel(currentStep, stepTitles)} could not be loaded`}>
          The encounter template didn&rsquo;t load, so this step can&rsquo;t be shown. Use Retry above. Assessment &amp;
          Plan and Wrap-up are still available.
        </Callout>
      ) : onQuestionnaireStep ? (
        /* No authored groups for this section. The EncounterScreen design renders exactly
           this case as its `isStub` block (lines 771-781): a 28px grey seat with a document
           glyph, the step name at 16px/700 --color-text-secondary, and "Not documented"
           at 12px --color-text-hint. That IS the design's Risk screen — Risk has no
           authored template, so the design shows the stub rather than a Risk surface.
           The old copy ("configure in Template Studio") is kept as the seat's title so the
           actionable detail is not lost, it just stops shouting on the canvas. */
        <EncounterStepEmpty label={stepLabel(currentStep, stepTitles)} />
      ) : currentStep === 'ap' && assessmentPlan ? (
        <AssessmentPlanView {...assessmentPlan} {...(apPart ? { part: apPart } : {})} {...(inNote ? { inNote } : {})} />
      ) : currentStep === ALLIED_PLAN_STEP && alliedPlan ? (
        // PROD-2040. A SIBLING branch to A/P above, not a modification of it: the A/P arm is
        // byte-identical to what it was, and only the new step key reaches this one.
        <AlliedHealthPlanView {...alliedPlan} {...(inNote ? { inNote } : {})} />
      ) : currentStep === NURSING_PLAN_STEP && nursingPlan ? (
        // [PROD-2949] The nurse's plan block (design desk D-66): its own step, the mirror of the
        // allied-health arm above, so in frame v2 the nurse's P reads plan block then Wrap-up block.
        <NursingPlanView
          {...nursingPlan}
          {...(inNote ? { inNote, findings: noteNursingFindings(questionnaire, response, resolveVitals, stepTitles, props.frameV2?.riskManaged) } : {})}
        />
      ) : currentStep === 'wrap-up' && wrapUp ? (
        // The patient-instructions row (DS VoiceNoteInput, wrap-up spec node 1736:159114) is
        // rendered by the DS `WrapUpInstructionsPanel` inside `WrapUpView`.
        <WrapUpView
          {...wrapUp}
          {...(bare ? { untitled: true } : {})}
          // PROD-3148: in the note, Patient instructions takes the note's phrases (`.`), as the note lines do.
          {...(inNote ? { inNote, phrases: props.frameV2?.phrases, author: toClinicalRole(role) } : {})}
        />
      ) : currentStep === 'docs' && docs ? (
        // Frame v2: inside the note's "Documents" block the list draws no section header of its own
        // (DocumentsChartTab `inNote`); the page hands the element, so the block's mode is added here.
        inNote && isValidElement<DocumentsChartTabProps>(docs) && docs.type === DocumentsChartTab ? cloneElement(docs, { inNote: true }) : docs
      ) : sharedResponse && currentStep === 'docs' ? (
        // PROD-2962 (OL-1308 F3, design desk): frame v2 never shows a clinician developer copy ("Doc — ships
        // next."). The page hands the Docs block its list once the visit's encounter id has resolved;
        // until then the documents are loading, and the block says so — but never forever (D-67).
        <DocsPending />
      ) : (
        <EncounterStepEmpty variant="pending" label={stepLabel(currentStep, stepTitles)} />
      )}
    </>
  );
}

/** Frame v2 header: Room · Reason for visit, the save state, the role and the Sign button on one line,
 *  the S·O·A·P ribbon on its own full-width line below. In the three-column frame the centre column is
 *  684px at 1440 (chart 320 + Intelligence 380), and the DS's single header row (visit strip, then the
 *  nav, then the save state) left the ribbon 397px for 622px of steps: Wrap-up sat off-screen behind a
 *  sideways scroll (measured on dev, 1 Oct 06:58). Stacked, the ribbon gets the whole column and fits
 *  from ~1426px up (chart open) and from ~1180px down to the 1384px collapse (chart rail).
 *
 *  The 6 Oct audit, all from R6c Encounter Frame (the approved note header):
 *   · FV2-05 — the role ("Physician", StatusChip neutral soft) and a labelled "Sign note" button (DS
 *     Button primary sm) at the line's end, so signing is never a 32px icon at the bottom of the page;
 *   · FV2-38 — a signed note says "Signed <D-45 date, time>" and never the draft's "Saved";
 *   · FV2-31 — a save refused because the note is signed reads "Not saved" here; the banner above the
 *     canvas carries the sentence once (it was shown twice, the header copy wrapping to an orphan);
 *   · FV2-02 — "Saved" in text-secondary like "Saving…" (the success green was 2.83:1 on white); superseded 22 Oct
 *     (Antonin's review): the resting "Saved" label is not shown at all;
 *   · FV2-07 — the header's content caps at the note's 760px measure and centres with it, so the ribbon
 *     stays left-aligned with the note (sheet §1b; R6c header "max-width:760px; margin:0 auto").
 *  Otherwise the save state renders exactly as the DS shell renders it (error > saving > saved > status). */
function FrameV2StepHeader({
  visitStrip,
  saveState,
  roleChip,
  sign,
  signedLine,
  signed,
  reveal,
  onRevealed,
  ruleC,
  children,
}: {
  visitStrip?: ReactNode;
  /** the header (ribbon) slides in once when the visit starts. */
  reveal?: boolean;
  onRevealed?: () => void;
  /** Long page: a hook for the bridge (the balanced side panels). */
  ruleC?: boolean;
  saveState: EncounterShellSaveState;
  roleChip?: string;
  sign?: { label: string; disabled: boolean; title: string; onSign: () => void; reason?: string };
  /** "Signed 5 Oct 2026, 22:00" — shown on a signed note in place of the save line. */
  signedLine?: string;
  /** The clinician's sign is recorded: a draft's "Saved" never shows. */
  signed?: boolean;
  children: ReactNode;
}) {
  const error = saveState.error;
  const errorText = typeof error === 'string' && isSignedRefusalSentence(error) ? NOT_SAVED : error;
  const line = errorText ? (
    <p className="m-0 text-status-error text-size-xs max-w-xs text-right">{errorText}</p>
  ) : saveState.saving ? (
    <p className="m-0 text-text-secondary text-size-xs">Saving…</p>
  ) : signed ? (
    signedLine ? <p className="m-0 text-text-secondary text-size-xs whitespace-nowrap">{signedLine}</p> : null
  ) : saveState.saved ? (
    // 22 Oct, Antonin's review: no "Saved" label above the step bar. The autosave and its state are unchanged; only the
    // resting label goes (Saving…, a refused or failed save, and the signed line still show).
    null
  ) : saveState.status ? (
    <p className="m-0 text-text-secondary text-size-xs">{saveState.status}</p>
  ) : null;
  const hasEnd = line != null || roleChip != null || sign != null;
  return (
    <div
      data-slot="frame-v2-step-header"
      data-reveal={reveal ? 'true' : undefined}
      data-rule-c={ruleC ? 'true' : undefined}
      onAnimationEnd={reveal ? onRevealed : undefined}
      className="flex flex-col gap-3 w-full min-w-full"
    >
      {visitStrip != null || hasEnd ? (
        <div className="flex flex-row items-center justify-between gap-4 min-w-0">
          <div className="min-w-0">{visitStrip}</div>
          {hasEnd && (
            <div className="shrink-0 flex flex-row items-center gap-3">
              {line != null && (
                <div aria-live="polite" data-testid="save-indicator" className="flex flex-col items-end gap-1">
                  {line}
                </div>
              )}
              {roleChip != null && (
                <StatusChip data-slot="note-role" semantic="neutral" tone="soft">
                  {roleChip}
                </StatusChip>
              )}
              {sign != null && (
                /* `?x=sign-reason`: the reason the last Sign click was refused, under the button (the toast stays). */
                <div className="flex flex-col items-end gap-1">
                  <Button
                    data-slot="note-sign"
                    variant="primary"
                    size="sm"
                    disabled={sign.disabled}
                    title={sign.disabled ? sign.title : undefined}
                    onClick={sign.onSign}
                  >
                    {sign.label}
                  </Button>
                  {sign.reason ? (
                    <p role="alert" data-slot="note-sign-reason" className="m-0 text-status-error text-size-xs max-w-xs text-right">
                      Not signed: {sign.reason}
                    </p>
                  ) : null}
                </div>
              )}
            </div>
          )}
        </div>
      ) : null}
      {children}
    </div>
  );
}

/** The header visit-info strip — ROOM/TIME + Reason for Visit, left of the tab nav (Flutter
 *  parity). WAVE 4 (L8): a THIN adapter over the DS `VisitInfoStrip` molecule (B5 — E1 nodes
 *  616:70010-70012); the hand-built caption/value blocks retired in the same motion. Renders
 *  nothing when neither a time nor a reason resolves (DS contract: cells render only when the
 *  data supplies them — no fabricated placeholders). The drill chevron is NOT passed: Flutter's
 *  chevron here is a functional save-toggle, not navigation — shipping it decorative would be
 *  anti-fiction. */
function visitInfoCells(visitInfo?: EncounterShellViewProps['visitInfo'], opts: { d45?: boolean } = {}): VisitInfoCell[] {
  // Frame v2: the house date format, D-45 (D Mon YYYY, 24-hour — "6 Oct 2026, 09:00"; audit FV2-25).
  const time = opts.d45 ? formatDateTimeD45(visitInfo?.startISO) : formatVisitTime(visitInfo?.startISO);
  const room = visitInfo?.room?.trim();
  const reason = visitInfo?.reason?.trim();
  const cells: VisitInfoCell[] = [];
  // Room is the EYEBROW over the visit time — the design's "Room 512 / Aug 05, 12:25p" cell
  // (line 297-300). It renders as soon as `room` resolves, which needs the appointment-detail
  // fetch (list endpoints carry only roomId, G7) — never a fabricated room name.
  if (time) {
    cells.push({ id: 'room-time', caption: room || 'Time', value: time });
  } else if (room) {
    // Room with no resolvable time: the room is still real information and used to be dropped
    // on the floor. It gets its own honest cell rather than an invented time value.
    cells.push({ id: 'room', caption: 'Room', value: room });
  }
  if (reason) cells.push({ id: 'rfv', caption: 'Reason for Visit', value: reason });
  return cells;
}
